import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../../services/api';
import { getReceiptUploadLabel, normalizeFormForReceiptLabel } from '../../../utils/receiptFormLabel';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { initMetaPixel, trackMetaEvent } from '../../../utils/metaPixel';
import { initTikTokPixel, trackTikTokEvent } from '../../../utils/tiktokPixel';
import '../../../styles/campaign-public.css';
import {
  CheckCircle,
  Info,
  Clock,
  XCircle
} from 'lucide-react';

// For static assets, use ASSET_URL in dev, hostname in prod
const RAW_ASSET_BASE_URL = process.env.REACT_APP_ASSET_URL || process.env.REACT_APP_HOSTNAME || '';
const ASSET_BASE_URL = (() => {
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    return RAW_ASSET_BASE_URL.replace(/^http:\/\//i, 'https://');
  }
  return RAW_ASSET_BASE_URL;
})();

// Helper function to construct secure URLs
const getSecureAssetUrl = (path) => {
  if (!path) return '';
  
  // If path is already a full URL, ensure it uses HTTPS
  if (path.startsWith('http://') || path.startsWith('https://')) {
    if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
      return path.replace(/^http:\/\//i, 'https://');
    }
    return path;
  }
  
  // If path starts with /, append to ASSET_BASE_URL
  if (path.startsWith('/')) {
    return `${ASSET_BASE_URL}${path}`;
  }
  
  return `${ASSET_BASE_URL}/${path}`;
};

/** Escape a font family name for use inside a CSS single-quoted string */
const escapeCssFontFamilyName = (name) => {
  if (!name || typeof name !== 'string') return '';
  return name.replace(/'/g, "\\'");
};

/**
 * Paragraph/body stack from campaign_font_settings (public API attaches as campaign.font_settings).
 * Keys off paragraph_font_file_url; font-family name must match @font-face in applyTheme
 * (paragraph_font_name or synthetic CampaignParagraphFont-{id}).
 */
const getCampaignParagraphFontFamily = (campaign) => {
  const fs = campaign?.font_settings;
  if (!fs?.paragraph_font_file_url) {
    return "'URW Geometric', sans-serif";
  }
  const id = campaign?.id || 'default';
  const name = fs.paragraph_font_name || `CampaignParagraphFont-${id}`;
  return `'${escapeCssFontFamilyName(name)}', sans-serif`;
};

const isEntryLimitError = (message = '') => {
  if (typeof message !== 'string') return false;
  const normalized = message.toLowerCase();
  return (
    normalized.includes('maximum entries reached') ||
    normalized.includes('entry limit') ||
    normalized.includes('total entries have been reached') ||
    normalized.includes('per-user total entry limit reached') ||
    (normalized.includes('maximum') && normalized.includes('entries') && normalized.includes('has been reached')) ||
    (normalized.includes('entries permitted') && normalized.includes('has been reached')) ||
    (normalized.includes('maximum') && normalized.includes('permitted per person'))
  );
};

const isReceiptImageQualityError = (message = '') => {
  if (typeof message !== 'string') return false;
  const normalized = message.toLowerCase();
  return (
    normalized.includes('receipt image quality is insufficient') ||
    normalized.includes('receipt image is unclear')
  );
};

const VIDEO_RECORDING_MAX_SIZE_MB = 5;
const VIDEO_RECORDING_MAX_SIZE_BYTES = VIDEO_RECORDING_MAX_SIZE_MB * 1024 * 1024;

const getSupportedVideoMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return '';

  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm'
  ];

  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || '';
};

const getVideoRecordingExtension = (mimeType = 'video/webm') => (
  mimeType.includes('mp4') ? 'mp4' : 'webm'
);

const escapeHtmlPlain = (s) =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * Thank you / success copy: first line or first <p> → title (h2); rest → body as <p> (plain) or leftover HTML.
 */
const parseThankYouSuccessMessage = (raw, defaultLine) => {
  const trimmed = String(raw).trim();
  if (!trimmed) {
    return { titlePlain: defaultLine, titleHtml: null, bodyHtml: null };
  }
  const hasMarkup = /<\/?[a-z][\s\S]*>/i.test(trimmed);

  if (!hasMarkup) {
    const lines = trimmed.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 0);
    const first = lines[0] || defaultLine;
    const rest = lines.slice(1);
    const bodyHtml =
      rest.length > 0 ? rest.map((line) => `<p>${escapeHtmlPlain(line)}</p>`).join('') : null;
    return { titlePlain: first, titleHtml: null, bodyHtml };
  }

  const m = /^<p(\s[^>]*)?>([\s\S]*?)<\/p>\s*([\s\S]*)$/i.exec(trimmed);
  if (m) {
    const titleInner = m[2].trim();
    const remainder = m[3].trim();
    return {
      titlePlain: null,
      titleHtml: titleInner.length ? titleInner : defaultLine,
      bodyHtml: remainder.length ? remainder : null,
    };
  }

  return {
    titlePlain: 'Thanks for taking part in the promotion.',
    titleHtml: null,
    bodyHtml: trimmed,
  };
};

function CampaignPublic() {
  const { slug } = useParams();
  const isBrowser = typeof window !== 'undefined';
  const currentHostname = isBrowser ? window.location.hostname : '';
  
  // Custom domain detection - admin domains show login, custom domains show campaigns
  const isCustomDomain = isBrowser ? 
    !window.location.hostname.includes('gooddrinkspromotion.dailypress.com.au') && 
    !window.location.hostname.includes('localhost') && 
    !window.location.hostname.includes('127.0.0.1') : false;
  const [campaign, setCampaign] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [form, setForm] = useState(null);
  const [formFields, setFormFields] = useState([]);
  const [allFields, setAllFields] = useState([]); // Combined system + custom fields
  const [sections, setSections] = useState([]); // New: campaign sections
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showThankYou, setShowThankYou] = useState(false);
  const [entryNumber, setEntryNumber] = useState('');
  const [entrySequence, setEntrySequence] = useState(null);
  const [entryStatus, setEntryStatus] = useState('');
  const [validationMessage, setValidationMessage] = useState('');
  const [instantWinResult, setInstantWinResult] = useState(null);
  const [cashbackVoucher, setCashbackVoucher] = useState(null);
  const [giftCard, setGiftCard] = useState(null);
  const [validationSteps, setValidationSteps] = useState(null);
  const [showValidationProgress, setShowValidationProgress] = useState(false);
  const [ocrMetadata, setOcrMetadata] = useState(null); // Store OCR confidence and extracted text
  const [parsingMethod, setParsingMethod] = useState(null);
  const [structuredReceipt, setStructuredReceipt] = useState(null);
  const [sectionOrder, setSectionOrder] = useState(['banner', 'content', 'faqs', 'gallery', 'terms-footer']);
  const [pageType, setPageType] = useState('landing');
  const [ageVerified, setAgeVerified] = useState(false);
  const [showAgeVerification, setShowAgeVerification] = useState(true);
  const [showUnderageMessage, setShowUnderageMessage] = useState(false);
  const [waitingForInstantWin, setWaitingForInstantWin] = useState(false);
  const [resolvedSlug, setResolvedSlug] = useState(slug || '');
  const [showTerms, setShowTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  
  // State for Entry Method 2 quantity inputs
  const [quantityInputs, setQuantityInputs] = useState({});
  const [videoRecordings, setVideoRecordings] = useState({});
  const [videoRecordingStatus, setVideoRecordingStatus] = useState({});
  const videoRecordingsRef = useRef({});
  const videoPreviewRefs = useRef({});
  const mediaRecorderRefs = useRef({});
  const mediaStreamRefs = useRef({});
  const mediaChunkRefs = useRef({});
  const instantWinPrizeValue = instantWinResult && !Number.isNaN(Number(instantWinResult.value))
    ? Number(instantWinResult.value)
    : null;
  const defaultThankYouLine = 'Thank you for participating!';
  const providedThankYouBody = form?.success_message ?? campaign?.thank_you_message ?? '';
  const thankYouRaw =
    typeof providedThankYouBody === 'string' && providedThankYouBody.trim()
      ? providedThankYouBody.trim()
      : defaultThankYouLine;

  const isInstantWinLoser =
    instantWinResult &&
    instantWinResult.eligible === true &&
    instantWinResult.won === false &&
    entryStatus === 'approved';

  const loserMessageRaw =
    isInstantWinLoser &&
    typeof campaign?.loser_message === 'string' &&
    campaign.loser_message.trim()
      ? campaign.loser_message.trim()
      : '';

  const thankYouDisplayRaw = loserMessageRaw || thankYouRaw;
  const thankYouParsed = parseThankYouSuccessMessage(thankYouDisplayRaw, defaultThankYouLine);

  const winnerMessageRaw =
    typeof campaign?.winner_message === 'string' && campaign.winner_message.trim()
      ? campaign.winner_message.trim()
      : '';
  const winnerParsed = winnerMessageRaw
    ? parseThankYouSuccessMessage(winnerMessageRaw, defaultThankYouLine)
    : null;

  const shadeColor = (hexColor, percent) => {
    let hex = hexColor.replace('#', '');
    if (hex.length === 3) {
      hex = hex.split('').map(char => char + char).join('');
    }
    const num = parseInt(hex, 16);
    const amt = Math.round(2.55 * percent);
    const r = Math.min(255, Math.max(0, (num >> 16) + amt));
    const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + amt));
    const b = Math.min(255, Math.max(0, (num & 0x0000ff) + amt));
    return `#${(0x1000000 + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  };

  const getParserDisplayName = (method) => {
    if (!method || typeof method !== 'string') return 'Unknown';
    const normalized = method.toLowerCase();

    switch (normalized) {
      case 'gemini-flash-lite-image':
        return 'Gemini 1.5 Flash Lite (image)';
      case 'gemini-flash-lite':
        return 'Gemini 1.5 Flash Lite';
      case 'gemini-flash':
        return 'Gemini 1.5 Flash';
      case 'openrouter':
        return 'OpenRouter (GPT-OSS 20B)';
      case 'nlp':
      case 'nlp-fallback':
        return 'NLP fallback parser';
      case 'ocr':
        return 'Traditional OCR';
      default:
        return method;
    }
  };

  const getStructuredReceiptText = () => {
    if (!structuredReceipt) return '';
    try {
      return JSON.stringify(structuredReceipt, null, 2);
    } catch (error) {
      console.error('Failed to stringify structured receipt:', error);
      return '';
    }
  };

  const handleCopyStructuredReceipt = () => {
    const text = getStructuredReceiptText();
    if (!text) return;

    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => window.alert('Structured receipt copied to clipboard!'))
        .catch(() => window.alert('Failed to copy structured receipt.'));
      return;
    }

    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      window.alert('Structured receipt copied to clipboard!');
    } catch (error) {
      window.alert('Failed to copy structured receipt.');
    }
  };

  const getYouTubeEmbedUrl = (rawUrl) => {
    if (!rawUrl || typeof rawUrl !== 'string') return null;

    let url = rawUrl.trim();
    if (!url) return null;

    if (!/^https?:\/\//i.test(url)) {
      url = `https://${url}`;
    }

    let videoId = '';

    try {
      const parsed = new URL(url);
      const hostname = parsed.hostname.replace(/^www\./i, '').toLowerCase();

      if (hostname === 'youtu.be') {
        videoId = parsed.pathname.split('/').filter(Boolean)[0] || '';
      } else if (hostname.endsWith('youtube.com')) {
        if (parsed.searchParams.has('v')) {
          videoId = parsed.searchParams.get('v') || '';
        } else if (parsed.pathname.startsWith('/embed/')) {
          videoId = parsed.pathname.split('/')[2] || '';
        } else if (parsed.pathname.startsWith('/shorts/')) {
          videoId = parsed.pathname.split('/')[2] || '';
        } else if (parsed.pathname.startsWith('/live/')) {
          videoId = parsed.pathname.split('/')[2] || '';
        }
      }
    } catch (error) {
      // Fall back to regex parsing below if URL constructor fails
    }

    if (!videoId || videoId.length < 11) {
      const match = url.match(/(?:v=|\/)([0-9A-Za-z_-]{11})(?:[?&/]|$)/);
      if (match && match[1]) {
        videoId = match[1];
      }
    }

    if (videoId) {
      videoId = videoId.replace(/[^0-9A-Za-z_-]/g, '');
    }

    if (!videoId || videoId.length !== 11) {
      return null;
    }

    return `https://www.youtube.com/embed/${videoId}`;
  };

  const renderSectionYouTubeVideo = (section, options = {}) => {
    const embedUrl = getYouTubeEmbedUrl(section?.youtube_video_url);
    if (!embedUrl) return null;

    const wrapperClass = options.wrapperClass ?? 'mt-8';
    const roundedClass = options.roundedClass ?? 'rounded-2xl';
    const shadowClass = options.shadowClass ?? 'shadow-lg';
    const innerClass = options.innerClass ?? '';

    const containerClasses = ['relative bg-black overflow-hidden', roundedClass, shadowClass, innerClass]
      .filter(Boolean)
      .join(' ');

    const embedSrc = embedUrl.includes('?')
      ? `${embedUrl}&rel=0&modestbranding=1`
      : `${embedUrl}?rel=0&modestbranding=1`;

    const videoTitle = section?.about_header
      ? `${section.about_header} video`
      : campaign?.name
        ? `${campaign.name} video`
        : 'Campaign video';

    return (
      <div className={wrapperClass}>
        <div className={containerClasses}>
          <div className="relative pt-[56.25%]">
            <iframe
              src={embedSrc}
              title={videoTitle}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              loading="lazy"
              className="absolute inset-0 h-full w-full border-0"
            />
          </div>
        </div>
      </div>
    );
  };

  const isComingSoonPage = pageType === 'coming_soon';
  
  // Initialize quantity inputs for Entry Method 2 fields
  useEffect(() => {
    const newQuantityInputs = {};
    formFields.forEach(field => {
      if (field.is_multiselect && field.enable_quantity_input && field.field_options?.options) {
        const fieldKey = field.field_name;
        newQuantityInputs[fieldKey] = {};
        field.field_options.options.forEach(option => {
          newQuantityInputs[fieldKey][option] = 0;
        });
      }
    });
    setQuantityInputs(newQuantityInputs);
  }, [formFields]);

  // Update product_entry_2 when quantity inputs change
  useEffect(() => {
    Object.keys(quantityInputs).forEach(fieldName => {
      const fieldQuantities = quantityInputs[fieldName];
      const drinkEntries = Object.entries(fieldQuantities)
        .filter(([drink, qty]) => qty > 0)
        .map(([drink, qty]) => `${drink}:${qty}`)
        .join(',');
      
      setFormData(prev => ({
        ...prev,
        [fieldName]: drinkEntries || ''
      }));
    });
  }, [quantityInputs]);

  useEffect(() => {
    videoRecordingsRef.current = videoRecordings;
  }, [videoRecordings]);

  useEffect(() => () => {
    Object.values(videoRecordingsRef.current).forEach((recording) => {
      if (recording?.previewUrl) {
        URL.revokeObjectURL(recording.previewUrl);
      }
    });

    Object.keys(mediaRecorderRefs.current).forEach((fieldName) => {
      const mediaRecorder = mediaRecorderRefs.current[fieldName];
      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        try {
          mediaRecorder.stop();
        } catch (error) {
          console.error('Failed to stop active media recorder:', error);
        }
      }
    });

    Object.values(mediaStreamRefs.current).forEach((stream) => {
      stream.getTracks().forEach((track) => track.stop());
    });
  }, []);

  // Track instant win state changes
  useEffect(() => {
    // State updated
  }, [instantWinResult]);

  useEffect(() => {
    if (slug) {
      setResolvedSlug(slug);
    } else if (!isCustomDomain) {
      setResolvedSlug('');
    }
  }, [slug, isCustomDomain]);

  // Handle showing thank you page after instant win result is set
  useEffect(() => {
    if (waitingForInstantWin && instantWinResult !== undefined) {
      setShowThankYou(true);
      setWaitingForInstantWin(false);
      
      // Reset form now that we're showing the thank you page
      const resetFormData = {};
      allFields.forEach(field => {
        if (field.field_type === 'checkbox' && !field.is_system) {
          resetFormData[field.field_name] = [];
        } else {
          resetFormData[field.field_name] = '';
        }
      });
      resetFormData.agree_terms = false;
      resetFormData.marketing_consent = true;
      setFormData(resetFormData);
      setReceiptFile(null);
      resetVideoRecordingState();
      setValidationSteps(null);
      setShowValidationProgress(false);
      setOcrMetadata(null);
      setEntrySequence(null);
      setEntryNumber('');
    }
  }, [waitingForInstantWin, instantWinResult, allFields]);

  // Check if age was already verified in this session
  useEffect(() => {
    const verified = sessionStorage.getItem('ageVerified');
    if (verified === 'true') {
      setAgeVerified(true);
      setShowAgeVerification(false);
    }
  }, []);

  // Form states
  const [formData, setFormData] = useState({});
  const [receiptFile, setReceiptFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const submitInFlightRef = useRef(false);
  const [formErrors, setFormErrors] = useState({});

  // Contact form states
  const [contactFormData, setContactFormData] = useState({
    full_name: '',
    email: '',
    message: ''
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactFormErrors, setContactFormErrors] = useState({});
  const [contactFormSuccess, setContactFormSuccess] = useState(false);


  const handleAgeVerification = (isOver18) => {
    if (isOver18) {
      setAgeVerified(true);
      setShowAgeVerification(false);
      sessionStorage.setItem('ageVerified', 'true');
    } else {
      setShowUnderageMessage(true);
    }
  };

  useEffect(() => {
    fetchCampaign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, isCustomDomain]);

  const fetchCampaign = async () => {
    try {
      setLoading(true);
      setError('');

      let response;
      if (slug) {
        response = await api.get(`/public/campaigns/${slug}`);
      } else if (isCustomDomain) {
        if (!currentHostname) {
          setError('Campaign not found or not active');
          setLoading(false);
          return;
        }
        response = await api.get(`/public/campaigns/domain/${encodeURIComponent(currentHostname)}`);
      } else {
        setError('Campaign not found or not active');
        setLoading(false);
        return;
      }

      const data = response.data;
      const actualSlug = data.campaign?.slug || slug || '';
      if (actualSlug) {
        setResolvedSlug(actualSlug);
      }
      
      // Check if we should redirect to winner page
      if (data.isWinnerPage && data.redirectTo === 'winner') {
        setLoading(false);
        if (isCustomDomain && data.campaign?.custom_domain) {
          window.location.href = '/winner';
        } else if (actualSlug) {
          window.location.href = `/campaign/${actualSlug}/winner`;
        } else if (slug) {
          window.location.href = `/campaign/${slug}/winner`;
        } else {
          window.location.href = '/winner';
        }
        return;
      }
      
      setCampaign(data.campaign);
      setGallery(data.gallery);
      setFaqs(data.faqs);
      setSections(data.sections || []); // Set sections from API
      const derivedPageType = data.pageType 
        || (data.isComingSoonPage ? 'coming_soon' : (data.campaign?.display_status === 'upcoming' ? 'coming_soon' : 'landing'));
      setPageType(derivedPageType);
      const normalizedForm = data.form ? normalizeFormForReceiptLabel(data.form) : null;
      setForm(normalizedForm);
      // Parse JSON field options if they're strings
      const parsedFields = (data.formFields || []).map(field => ({
        ...field,
        field_options: typeof field.field_options === 'string' 
          ? JSON.parse(field.field_options) 
          : field.field_options
      }));
      setFormFields(parsedFields);
      
      // Get saved system field positions from localStorage
      const savedPositions = normalizedForm ? localStorage.getItem(`form_${normalizedForm.id}_system_positions`) : null;
      const systemPositions = savedPositions ? JSON.parse(savedPositions) : {};
      const systemFieldOverrides = normalizedForm?.system_fields_config || {};
      
      // Get deleted system fields from localStorage
      const deletedSystemFields = normalizedForm ? JSON.parse(localStorage.getItem(`form_${normalizedForm.id}_deleted_system_fields`) || '[]') : [];

      const buildSystemField = (fieldId, defaults) => {
        const overrides = systemFieldOverrides[fieldId] || {};
        const merged = {
          ...defaults,
          ...overrides
        };
        if (overrides.hasOwnProperty('is_required')) {
          merged.is_required = !!overrides.is_required;
        }
        // Force phone field to be required
        if (fieldId === 'system_phone') {
          merged.is_required = true;
        }
        merged.field_placeholder = overrides.field_placeholder ?? defaults.field_placeholder ?? '';
        merged.is_system = true;
        merged.field_name = defaults.field_name;
        merged.field_type = defaults.field_type;
        merged.sort_order = defaults.sort_order;
        return merged;
      };
      
      // Define all available system fields
      const allSystemFieldsDefinitions = [
        {
          id: 'system_name',
          field_type: 'text',
          field_name: 'name',
          field_label: 'Full Name',
          is_required: true,
          sort_order: systemPositions.system_name ?? 0,
          field_placeholder: ''
        },
        {
          id: 'system_email',
          field_type: 'email',
          field_name: 'email',
          field_label: 'Email Address',
          is_required: true,
          sort_order: systemPositions.system_email ?? 1,
          field_placeholder: ''
        },
        {
          id: 'system_phone',
          field_type: 'phone',
          field_name: 'phone',
          field_label: 'Phone Number',
          is_required: true,
          sort_order: systemPositions.system_phone ?? 2,
          field_placeholder: ''
        }
      ];
      
      // Build only non-deleted system fields
      const systemFields = allSystemFieldsDefinitions
        .filter(def => !deletedSystemFields.includes(def.id))
        .map(def => buildSystemField(def.id, def));
      
      // Combine and sort all fields
      const combinedFields = [...systemFields, ...parsedFields].sort((a, b) => a.sort_order - b.sort_order);
      setAllFields(combinedFields);
      
      // Initialize form data with all fields
      const initialFormData = {};
      combinedFields.forEach(field => {
        if (field.field_type === 'checkbox' && !field.is_system) {
          initialFormData[field.field_name] = [];
        } else {
          initialFormData[field.field_name] = '';
        }
      });
      initialFormData.agree_terms = false;
      initialFormData.marketing_consent = true;
      setFormData(initialFormData);
      resetVideoRecordingState();
      
      // Get section order - use sections if available, otherwise fall back to theme
      if (data.sections && data.sections.length > 0) {
        // Use the new sections system - sections are already ordered by sort_order
        setSectionOrder(data.sections.map(section => section.type));
      } else if (data.campaign?.section_order) {
        // Fallback to legacy section order from theme
        const order = typeof data.campaign.section_order === 'string' 
          ? JSON.parse(data.campaign.section_order) 
          : data.campaign.section_order;
        setSectionOrder(order);
      }
      
      setLoading(false);
      
      // Apply theme
      applyTheme(data.campaign);
    } catch (err) {
      setError('Campaign not found or not active');
      setLoading(false);
    }
  };

  // Meta Pixel: load the campaign's pixel (PageView)
  const metaPixelId = campaign?.meta_pixel_id || '';
  useEffect(() => {
    if (metaPixelId) initMetaPixel(metaPixelId);
  }, [metaPixelId]);

  // TikTok Pixel: load the campaign's pixel (page view)
  const tiktokPixelId = campaign?.tiktok_pixel_id || '';
  useEffect(() => {
    if (tiktokPixelId) initTikTokPixel(tiktokPixelId);
  }, [tiktokPixelId]);

  // Re-apply fonts when campaign changes (in case font_settings are loaded asynchronously)
  useEffect(() => {
    if (campaign && campaign.font_settings) {
      applyTheme(campaign);
    }
  }, [campaign?.font_settings]);


  const applyTheme = (campaign) => {
    const root = document.documentElement;
    if (campaign.primary_color) {
      root.style.setProperty('--primary-color', campaign.primary_color);
      try {
        const shade = shadeColor(campaign.primary_color, -10);
        root.style.setProperty('--primary-color-dark', shade);
      } catch (e) {
        root.style.setProperty('--primary-color-dark', campaign.primary_color);
      }
    }
    if (campaign.secondary_color) {
      root.style.setProperty('--secondary-color', campaign.secondary_color);
    }
    if (campaign.accent_color) {
      root.style.setProperty('--accent-color', campaign.accent_color);
    }
    
    // Update favicon and apple-touch-icon if campaign logo exists
    if (campaign.logo_url) {
      const faviconUrl = getSecureAssetUrl(campaign.logo_url);
      
      // Update favicon
      let favicon = document.querySelector("link[rel*='icon']");
      if (!favicon) {
        favicon = document.createElement('link');
        favicon.rel = 'icon';
        document.head.appendChild(favicon);
      }
      favicon.href = faviconUrl;
      
      // Update apple-touch-icon
      let appleTouchIcon = document.querySelector("link[rel*='apple-touch-icon']");
      if (!appleTouchIcon) {
        appleTouchIcon = document.createElement('link');
        appleTouchIcon.rel = 'apple-touch-icon';
        document.head.appendChild(appleTouchIcon);
      }
      appleTouchIcon.href = faviconUrl;
    }
    
    // Apply custom fonts
    if (campaign.font_settings) {
      const fontStyleId = 'campaign-custom-fonts';
      let fontStyleEl = document.getElementById(fontStyleId);
      if (!fontStyleEl) {
        fontStyleEl = document.createElement('style');
        fontStyleEl.id = fontStyleId;
        document.head.appendChild(fontStyleEl);
      }
      
      let fontFaceCSS = '';
      const campaignId = campaign.id || 'default';
      
      // Helper function to escape font names for CSS (escape single quotes)
      const escapeFontName = (name) => {
        if (!name) return name;
        // Replace single quotes with escaped version for use in CSS strings
        return name.replace(/'/g, "\\'");
      };
      
      // Helper function to get font format from URL
      const getFontFormat = (fontUrl) => {
        if (!fontUrl) return 'truetype';
        const fontExt = fontUrl.toLowerCase().split('.').pop();
        return fontExt === 'otf' ? 'opentype' : 
               (fontExt === 'ttf' ? 'truetype' : 
               (fontExt === 'woff' ? 'woff' : 
               (fontExt === 'woff2' ? 'woff2' : 'truetype')));
      };
      
      // Header font
      if (campaign.font_settings && campaign.font_settings.header_font_file_url) {
        const rawHeaderFontUrl = campaign.font_settings.header_font_file_url;
        const headerFontUrl = getSecureAssetUrl(rawHeaderFontUrl);
        const rawHeaderFontName = campaign.font_settings.header_font_name || `CampaignHeaderFont-${campaignId}`;
        
        // Validate URL
        if (!headerFontUrl || headerFontUrl.trim() === '') {
          console.error('Header font URL is empty after processing. Original URL:', rawHeaderFontUrl);
        } else {
          // Escape font name for use in both @font-face and CSS font-family
          const headerFontNameEscaped = escapeFontName(rawHeaderFontName);
          
          // Escape URL for CSS (escape single quotes and parentheses)
          const escapedHeaderFontUrl = headerFontUrl.replace(/'/g, "\\'").replace(/\(/g, "\\(").replace(/\)/g, "\\)");
          const headerFontFormat = getFontFormat(headerFontUrl);
          
          fontFaceCSS += `
          @font-face {
            font-family: '${headerFontNameEscaped}';
            src: url('${escapedHeaderFontUrl}') format('${headerFontFormat}');
          }
        `;
          root.style.setProperty('--header-font-family', `'${headerFontNameEscaped}', serif`);
        }
      } else {
        console.warn('Header font file URL is missing in font_settings:', campaign.font_settings);
      }
      
      // Paragraph font
      if (campaign.font_settings && campaign.font_settings.paragraph_font_file_url) {
        const rawParagraphFontUrl = campaign.font_settings.paragraph_font_file_url;
        const paragraphFontUrl = getSecureAssetUrl(rawParagraphFontUrl);
        const rawParagraphFontName = campaign.font_settings.paragraph_font_name || `CampaignParagraphFont-${campaignId}`;
        
        // Validate URL
        if (!paragraphFontUrl || paragraphFontUrl.trim() === '') {
          console.error('Paragraph font URL is empty after processing. Original URL:', rawParagraphFontUrl);
        } else {
          // Escape font name for use in both @font-face and CSS font-family
          const paragraphFontNameEscaped = escapeFontName(rawParagraphFontName);
          
          // Escape URL for CSS (escape single quotes and parentheses)
          const escapedParagraphFontUrl = paragraphFontUrl.replace(/'/g, "\\'").replace(/\(/g, "\\(").replace(/\)/g, "\\)");
          const paragraphFontFormat = getFontFormat(paragraphFontUrl);
          
          fontFaceCSS += `
          @font-face {
            font-family: '${paragraphFontNameEscaped}';
            src: url('${escapedParagraphFontUrl}') format('${paragraphFontFormat}');
          }
        `;
          root.style.setProperty('--paragraph-font-family', `'${paragraphFontNameEscaped}', sans-serif`);
        }
      } else {
        console.warn('Paragraph font file URL is missing in font_settings:', campaign.font_settings);
      }
      
      // Set font-face CSS first
      fontStyleEl.textContent = fontFaceCSS;
      
      // Apply font styles immediately to prevent FOUT (Flash of Unstyled Text)
      // The browser will use the custom font as soon as it loads
      const applyFontStyles = () => {
        // Apply paragraph font first (if exists), then header font will override headings
        if (campaign.font_settings.paragraph_font_file_url) {
          const rawParagraphFontName = campaign.font_settings.paragraph_font_name || `CampaignParagraphFont-${campaignId}`;
          const paragraphFontNameEscaped = escapeFontName(rawParagraphFontName);
          const paragraphStyle = document.createElement('style');
          paragraphStyle.id = 'campaign-paragraph-font-style';
          // Higher-specificity overrides for campaign-public.css (.content-with-background-overlay .prose …)
          // which uses Greycliff with !important and would otherwise beat .campaign-public-page *.
          const overlayProseParagraphFont = `
            .campaign-public-page .content-with-background-overlay .prose strong,
            .campaign-public-page .content-with-background-overlay .prose b,
            .campaign-public-page .content-with-background-overlay .prose .p1,
            .campaign-public-page .content-with-background-overlay .prose p.p1,
            .campaign-public-page .content-with-background-overlay .prose [style*="font-weight: bold"],
            .campaign-public-page .content-with-background-overlay .prose [style*="font-weight:700"],
            .campaign-public-page .content-with-background-overlay .prose [style*="font-weight: 700"],
            .campaign-public-page .content-with-background-overlay .prose [style*="font-weight:bold"],
            .campaign-public-page .content-with-background-overlay .prose [style*="font-weight: 900"],
            .campaign-public-page .content-with-background-overlay .prose [style*="font-weight:900"],
            .campaign-public-page .content-with-background-overlay .prose p:first-child,
            .campaign-public-page .content-with-background-overlay .prose p:nth-child(1),
            .campaign-public-page .content-with-background-overlay .prose p[class*="p1"],
            .campaign-public-page .content-with-background-overlay .prose p[class*="bold"],
            .campaign-public-page .content-with-background-overlay .prose p[class*="strong"] {
              font-family: '${paragraphFontNameEscaped}', sans-serif !important;
            }
          `;
          paragraphStyle.textContent = `
            .campaign-public-page,
            .campaign-public-page * {
              font-family: '${paragraphFontNameEscaped}', sans-serif !important;
            }
            ${overlayProseParagraphFont}
          `;
          // Remove old style if exists
          const oldParagraphStyle = document.getElementById('campaign-paragraph-font-style');
          if (oldParagraphStyle) {
            oldParagraphStyle.remove();
          }
          document.head.appendChild(paragraphStyle);
        } else {
          const staleParagraphStyle = document.getElementById('campaign-paragraph-font-style');
          if (staleParagraphStyle) staleParagraphStyle.remove();
        }
        
        // Header font - apply after paragraph font so it overrides headings
        // Use very specific selectors with !important to override App.css
        if (campaign.font_settings.header_font_file_url) {
          const rawHeaderFontName = campaign.font_settings.header_font_name || `CampaignHeaderFont-${campaignId}`;
          const headerFontNameEscaped = escapeFontName(rawHeaderFontName);
          const headerStyle = document.createElement('style');
          headerStyle.id = 'campaign-header-font-style';
          headerStyle.textContent = `
            .campaign-public-page h1,
            .campaign-public-page h1.font-bold,
            .campaign-public-page h2,
            .campaign-public-page h2.font-bold,
            .campaign-public-page h3,
            .campaign-public-page h4,
            .campaign-public-page h5,
            .campaign-public-page h6,
            div.campaign-public-page h1,
            div.campaign-public-page h1.font-bold,
            div.campaign-public-page h2,
            div.campaign-public-page h2.font-bold,
            div.campaign-public-page h3,
            div.campaign-public-page h4,
            div.campaign-public-page h5,
            div.campaign-public-page h6,
            .campaign-public-page .content-with-background-overlay .prose h1,
            .campaign-public-page .content-with-background-overlay .prose h1.font-bold,
            .campaign-public-page .content-with-background-overlay .prose h2,
            .campaign-public-page .content-with-background-overlay .prose h2.font-bold,
            .campaign-public-page .content-with-background-overlay .prose h3,
            .campaign-public-page .content-with-background-overlay .prose h4,
            .campaign-public-page .content-with-background-overlay .prose h5,
            .campaign-public-page .content-with-background-overlay .prose h6 {
              font-family: '${headerFontNameEscaped}', serif !important;
            }
          `;
          // Remove old style if exists
          const oldHeaderStyle = document.getElementById('campaign-header-font-style');
          if (oldHeaderStyle) {
            oldHeaderStyle.remove();
          }
          document.head.appendChild(headerStyle);
        }
      };
      
      // Apply font styles immediately (don't wait for fonts to load)
      // This prevents FOUT - the browser will use the custom font as soon as it's available
      applyFontStyles();
      
      // Verify font loading and apply styles
      const verifyAndApplyFonts = () => {
        if (document.fonts && document.fonts.load) {
          // Use document.fonts.load() to explicitly load fonts and wait for them
          const fontPromises = [];
          
          if (campaign.font_settings.header_font_file_url) {
            const rawHeaderFontName = campaign.font_settings.header_font_name || `CampaignHeaderFont-${campaignId}`;
            const headerFontNameEscaped = escapeFontName(rawHeaderFontName);
            const headerFontSpec = `12px '${headerFontNameEscaped}'`;
            fontPromises.push(document.fonts.load(headerFontSpec).catch(err => {
              console.warn('Header font load error:', err);
              return null; // Continue even if font fails to load
            }));
          }
          
          if (campaign.font_settings.paragraph_font_file_url) {
            const rawParagraphFontName = campaign.font_settings.paragraph_font_name || `CampaignParagraphFont-${campaignId}`;
            const paragraphFontNameEscaped = escapeFontName(rawParagraphFontName);
            const paragraphFontSpec = `12px '${paragraphFontNameEscaped}'`;
            fontPromises.push(document.fonts.load(paragraphFontSpec).catch(err => {
              console.warn('Paragraph font load error:', err);
              return null; // Continue even if font fails to load
            }));
          }
          
          if (fontPromises.length > 0) {
            // Wait for all fonts to load (or timeout after 5 seconds)
            const timeoutPromise = new Promise((resolve) => {
              setTimeout(() => {
                console.warn('Font loading timeout - applying styles anyway');
                resolve(null);
              }, 5000);
            });
            
            Promise.race([
              Promise.all(fontPromises),
              timeoutPromise
            ])
              .then(() => {
                // Double-check fonts are actually loaded
                const checkFonts = () => {
                  let allFontsLoaded = true;
                  
                  if (campaign.font_settings.header_font_file_url) {
                    const rawHeaderFontName = campaign.font_settings.header_font_name || `CampaignHeaderFont-${campaignId}`;
                    const headerFontNameEscaped = escapeFontName(rawHeaderFontName);
                    // Try multiple formats for font checking
                    const headerLoaded = document.fonts.check(`12px '${headerFontNameEscaped}'`) ||
                                      document.fonts.check(`12px ${headerFontNameEscaped}`) ||
                                      document.fonts.check(`normal 12px '${headerFontNameEscaped}'`);
                    if (!headerLoaded) {
                      allFontsLoaded = false;
                    }
                  }
                  
                  if (campaign.font_settings.paragraph_font_file_url) {
                    const rawParagraphFontName = campaign.font_settings.paragraph_font_name || `CampaignParagraphFont-${campaignId}`;
                    const paragraphFontNameEscaped = escapeFontName(rawParagraphFontName);
                    // Try multiple formats for font checking
                    const paragraphLoaded = document.fonts.check(`12px '${paragraphFontNameEscaped}'`) ||
                                          document.fonts.check(`12px ${paragraphFontNameEscaped}`) ||
                                          document.fonts.check(`normal 12px '${paragraphFontNameEscaped}'`);
                    if (!paragraphLoaded) {
                      allFontsLoaded = false;
                    }
                  }
                  
                  if (allFontsLoaded) {
                    applyFontStyles();
                  } else {
                    // Retry checking after a short delay (max 10 retries = 2 seconds)
                    let retryCount = 0;
                    const maxRetries = 10;
                    const retryCheck = () => {
                      retryCount++;
                      let stillLoading = false;
                      
                      if (campaign.font_settings.header_font_file_url) {
                        const rawHeaderFontName = campaign.font_settings.header_font_name || `CampaignHeaderFont-${campaignId}`;
                        const headerFontNameEscaped = escapeFontName(rawHeaderFontName);
                        const headerLoaded = document.fonts.check(`12px '${headerFontNameEscaped}'`) ||
                                          document.fonts.check(`12px ${headerFontNameEscaped}`);
                        if (!headerLoaded) stillLoading = true;
                      }
                      
                      if (campaign.font_settings.paragraph_font_file_url) {
                        const rawParagraphFontName = campaign.font_settings.paragraph_font_name || `CampaignParagraphFont-${campaignId}`;
                        const paragraphFontNameEscaped = escapeFontName(rawParagraphFontName);
                        const paragraphLoaded = document.fonts.check(`12px '${paragraphFontNameEscaped}'`) ||
                                              document.fonts.check(`12px ${paragraphFontNameEscaped}`);
                        if (!paragraphLoaded) stillLoading = true;
                      }
                      
                      if (!stillLoading || retryCount >= maxRetries) {
                        applyFontStyles();
                      } else {
                        setTimeout(retryCheck, 200);
                      }
                    };
                    setTimeout(retryCheck, 200);
                  }
                };
                
                // Start checking after a brief delay to allow fonts to register
                setTimeout(checkFonts, 100);
              })
              .catch((err) => {
                console.error('Font loading error:', err);
                // Fallback: apply styles anyway after a delay
                setTimeout(applyFontStyles, 500);
              });
          } else {
            // No fonts to load, apply styles immediately
            applyFontStyles();
          }
        } else if (document.fonts && document.fonts.ready) {
          // Fallback: use document.fonts.ready if available
          document.fonts.ready.then(() => {
            applyFontStyles();
          }).catch((err) => {
            console.error('Font loading error:', err);
            // Fallback if fonts.ready fails
            setTimeout(applyFontStyles, 500);
          });
        } else {
          // Fallback for browsers without document.fonts API
          setTimeout(applyFontStyles, 1000);
        }
      };
      
      // Start font verification
      verifyAndApplyFonts();
    }
    
    // Apply custom CSS
    if (campaign.custom_css) {
      const styleId = 'campaign-custom-styles';
      let styleEl = document.getElementById(styleId);
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = styleId;
        document.head.appendChild(styleEl);
      }
      styleEl.textContent = campaign.custom_css;
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    setFormErrors(prev => ({ ...prev, [name]: '' }));
    
    // Clear max entries error if user changes email or phone
    if ((name === 'email' || name === 'phone') && formErrors.submit) {
      if (isEntryLimitError(formErrors.submit)) {
        setFormErrors(prev => ({ ...prev, submit: '' }));
      }
    }
  };

  const stopVideoStream = (fieldName) => {
    const stream = mediaStreamRefs.current[fieldName];
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      delete mediaStreamRefs.current[fieldName];
    }

    const previewEl = videoPreviewRefs.current[fieldName];
    if (previewEl) {
      previewEl.srcObject = null;
    }
  };

  const clearVideoRecording = (fieldName) => {
    const existing = videoRecordings[fieldName];
    if (existing?.previewUrl) {
      URL.revokeObjectURL(existing.previewUrl);
    }

    stopVideoStream(fieldName);
    delete mediaRecorderRefs.current[fieldName];
    delete mediaChunkRefs.current[fieldName];

    setVideoRecordings((prev) => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });

    setVideoRecordingStatus((prev) => ({
      ...prev,
      [fieldName]: {
        isRecording: false,
        isInitializing: false
      }
    }));

    setFormErrors((prev) => ({ ...prev, [fieldName]: '' }));
  };

  const startVideoRecording = async (field) => {
    const fieldName = field.field_name;

    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setFormErrors((prev) => ({
        ...prev,
        [fieldName]: 'Video recording is not supported in this browser.'
      }));
      return;
    }

    try {
      clearVideoRecording(fieldName);

      setVideoRecordingStatus((prev) => ({
        ...prev,
        [fieldName]: {
          isRecording: false,
          isInitializing: true
        }
      }));

      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });

      mediaStreamRefs.current[fieldName] = stream;

      const previewEl = videoPreviewRefs.current[fieldName];
      if (previewEl) {
        previewEl.srcObject = stream;
        previewEl.muted = true;
        previewEl.play().catch(() => {});
      }

      const mimeType = getSupportedVideoMimeType();
      const mediaRecorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      mediaChunkRefs.current[fieldName] = [];
      mediaRecorderRefs.current[fieldName] = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          mediaChunkRefs.current[fieldName].push(event.data);
        }
      };

      mediaRecorder.onerror = () => {
        stopVideoStream(fieldName);
        setVideoRecordingStatus((prev) => ({
          ...prev,
          [fieldName]: {
            isRecording: false,
            isInitializing: false
          }
        }));
        setFormErrors((prev) => ({
          ...prev,
          [fieldName]: 'Unable to record video. Please try again.'
        }));
      };

      mediaRecorder.onstop = () => {
        const chunks = mediaChunkRefs.current[fieldName] || [];
        const outputMimeType = mediaRecorder.mimeType || mimeType || 'video/webm';
        const blob = new Blob(chunks, { type: outputMimeType });

        stopVideoStream(fieldName);
        delete mediaRecorderRefs.current[fieldName];
        delete mediaChunkRefs.current[fieldName];

        if (!blob.size) {
          setFormErrors((prev) => ({
            ...prev,
            [fieldName]: 'No video was captured. Please try recording again.'
          }));
          setVideoRecordingStatus((prev) => ({
            ...prev,
            [fieldName]: {
              isRecording: false,
              isInitializing: false
            }
          }));
          return;
        }

        if (blob.size > VIDEO_RECORDING_MAX_SIZE_BYTES) {
          setFormErrors((prev) => ({
            ...prev,
            [fieldName]: `Video must be smaller than ${VIDEO_RECORDING_MAX_SIZE_MB}MB.`
          }));
          setVideoRecordingStatus((prev) => ({
            ...prev,
            [fieldName]: {
              isRecording: false,
              isInitializing: false
            }
          }));
          return;
        }

        const previous = videoRecordings[fieldName];
        if (previous?.previewUrl) {
          URL.revokeObjectURL(previous.previewUrl);
        }

        const previewUrl = URL.createObjectURL(blob);
        const extension = getVideoRecordingExtension(outputMimeType);
        const file = new File([blob], `${fieldName}-${Date.now()}.${extension}`, {
          type: outputMimeType
        });

        setVideoRecordings((prev) => ({
          ...prev,
          [fieldName]: {
            file,
            previewUrl,
            mimeType: outputMimeType,
            size: blob.size
          }
        }));

        setVideoRecordingStatus((prev) => ({
          ...prev,
          [fieldName]: {
            isRecording: false,
            isInitializing: false
          }
        }));

        setFormErrors((prev) => ({ ...prev, [fieldName]: '' }));
      };

      mediaRecorder.start(250);
      setVideoRecordingStatus((prev) => ({
        ...prev,
        [fieldName]: {
          isRecording: true,
          isInitializing: false
        }
      }));
      setFormErrors((prev) => ({ ...prev, [fieldName]: '' }));
    } catch (recordingError) {
      stopVideoStream(fieldName);
      delete mediaRecorderRefs.current[fieldName];
      delete mediaChunkRefs.current[fieldName];
      setVideoRecordingStatus((prev) => ({
        ...prev,
        [fieldName]: {
          isRecording: false,
          isInitializing: false
        }
      }));
      setFormErrors((prev) => ({
        ...prev,
        [fieldName]: 'Camera and microphone access is required to record a video.'
      }));
      console.error('Failed to start video recording:', recordingError);
    }
  };

  const stopVideoRecording = (fieldName) => {
    const mediaRecorder = mediaRecorderRefs.current[fieldName];
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
  };

  const resetVideoRecordingState = () => {
    Object.values(videoRecordingsRef.current).forEach((recording) => {
      if (recording?.previewUrl) {
        URL.revokeObjectURL(recording.previewUrl);
      }
    });

    Object.keys(mediaRecorderRefs.current).forEach((fieldName) => {
      const mediaRecorder = mediaRecorderRefs.current[fieldName];
      if (mediaRecorder) {
        mediaRecorder.ondataavailable = null;
        mediaRecorder.onerror = null;
        mediaRecorder.onstop = null;
        if (mediaRecorder.state !== 'inactive') {
          try {
            mediaRecorder.stop();
          } catch (error) {
            console.error('Failed to stop media recorder during reset:', error);
          }
        }
      }
      stopVideoStream(fieldName);
    });

    mediaRecorderRefs.current = {};
    mediaStreamRefs.current = {};
    mediaChunkRefs.current = {};
    videoPreviewRefs.current = {};
    videoRecordingsRef.current = {};
    setVideoRecordings({});
    setVideoRecordingStatus({});
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        setFormErrors({ receipt: 'Only JPG, PNG, and PDF files are allowed' });
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setFormErrors({ receipt: 'File size must be less than 10MB' });
        return;
      }
      setReceiptFile(file);
      setFormErrors(prev => ({ ...prev, receipt: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Validate all visible fields (system + custom)
    allFields.forEach(field => {
      if (field.is_required && isFieldVisible(field)) {
        const value = formData[field.field_name];
        const recording = videoRecordings[field.field_name];
        const status = videoRecordingStatus[field.field_name];

        if (field.field_type === 'video_recorder') {
          if (status?.isRecording || status?.isInitializing) {
            errors[field.field_name] = `Please finish recording ${field.field_label.toLowerCase()} before submitting`;
          } else if (!recording?.file) {
            errors[field.field_name] = `${field.field_label} is required`;
          }
          return;
        }

        if (!value || (typeof value === 'string' && !value.trim())) {
          errors[field.field_name] = `${field.field_label} is required`;
        } else if (field.field_type === 'phone' && typeof value === 'string' && value.trim().length <= 4) {
          errors[field.field_name] = `Please enter a valid ${field.field_label}`;
        } else if (field.field_type === 'email' && !/\S+@\S+\.\S+/.test(value)) {
          errors[field.field_name] = 'Email is invalid';
        }
      }
    });
    
    if (campaign?.requires_receipt && !receiptFile) errors.receipt = 'Receipt is required';
    if (!formData.agree_terms) errors.agree_terms = 'You must agree to the terms';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // A ref (not state) so a second click in the same tick, before React re-renders, is also ignored
    if (submitInFlightRef.current) return;
    if (!validateForm()) return;

    submitInFlightRef.current = true;
    setSubmitting(true);

    // Reset instant win / cashback result for new submission
    setInstantWinResult(undefined);
    setCashbackVoucher(null);
    setOcrMetadata(null);
    setParsingMethod(null);
    setStructuredReceipt(null);
    setValidationMessage('');
    
    // Store timeout IDs for simulated progress (clear when backend provides real steps)
    let step1Timeout = null;
    let step2Timeout = null;
    
    // Show validation progress if receipt is being uploaded
    if (receiptFile) {
      setShowValidationProgress(true);
      
      // Initial state - Step 1 in progress
      setValidationSteps({
        step1: {
          name: 'READING RECEIPT DATA AND EXTRACTING PRODUCTS.',
          status: 'in_progress',
          message: 'Extracting data from the receipt...'
        },
        step2: {
          name: 'PRODUCT VERIFICATION',
          status: 'pending',
          message: 'Waiting...'
        },
        step3: {
          name: 'FINALIZING RESULTS',
          status: 'pending',
          message: 'Waiting...'
        }
      });
      
      // Store timeout IDs to clear them if backend provides real steps
      // Simulate Step 1 completion and Step 2 start after 2 seconds
      step1Timeout = setTimeout(() => {
        setValidationSteps(prev => {
          // Only update if we still have the initial steps (not replaced by backend)
          if (prev && prev.step1 && prev.step1.status === 'in_progress') {
            return {
              ...prev,
              step1: {
                ...prev.step1,
                status: 'completed',
                message: 'Receipt data extracted'
              },
              step2: {
                ...prev.step2,
                status: 'in_progress',
                message: 'Checking product on receipt...'
              }
            };
          }
          return prev;
        });
      }, 2000);
      
      // Simulate Step 2 completion and Step 3 start after 4 seconds
      step2Timeout = setTimeout(() => {
        setValidationSteps(prev => {
          // Only update if we still have simulated steps (not replaced by backend)
          if (prev && prev.step2 && prev.step2.status === 'in_progress' && prev.step2.message === 'Checking product on receipt...') {
            return {
              ...prev,
              step2: {
                ...prev.step2,
                status: 'completed',
                message: 'Product found on receipt'
              },
              step3: {
                ...prev.step3,
                status: 'in_progress',
                message: 'Validating in campaign database...'
              }
            };
          }
          return prev;
        });
      }, 4000);
    }
    
    const formDataToSend = new FormData();
    
    // Separate system fields and custom fields for backend
    const systemFieldData = {};
    const customFieldData = {};
    
    allFields.forEach(field => {
      if (field.is_system) {
        systemFieldData[field.field_name] = formData[field.field_name];
      } else if (field.field_type === 'video_recorder') {
        const recording = videoRecordings[field.field_name];
        if (recording?.file) {
          formDataToSend.append(`video_recording_${field.field_name}`, recording.file, recording.file.name);
        }
      } else {
        customFieldData[field.field_name] = formData[field.field_name];
      }
    });
    
    // Add system fields
    formDataToSend.append('name', systemFieldData.name || '');
    formDataToSend.append('email', systemFieldData.email || '');
    formDataToSend.append('phone', systemFieldData.phone || '');
    formDataToSend.append('agree_terms', formData.agree_terms);
    formDataToSend.append('marketing_consent', formData.marketing_consent || false);
    
    if (receiptFile) {
      formDataToSend.append('receipt', receiptFile);
    }
    
    // Add dynamic form data
    if (form && Object.keys(customFieldData).length > 0) {
      formDataToSend.append('form_id', form.id);
      formDataToSend.append('form_fields', JSON.stringify(customFieldData));
    }

    try {
      // Update step 3 to show we're validating in database
      if (receiptFile && showValidationProgress) {
        setValidationSteps(prev => prev ? ({
          ...prev,
          step3: {
            ...prev.step3,
            status: 'in_progress',
            message: 'Validating in campaign database...'
          }
        }) : null);
      }
      
      const submissionSlug = resolvedSlug || slug;
      if (!submissionSlug) {
        setFormErrors(prev => ({ ...prev, submit: 'Campaign information is missing. Please refresh the page and try again.' }));
        submitInFlightRef.current = false;
        setSubmitting(false);
        setShowValidationProgress(false);
        return;
      }

      const response = await api.post(
        `/public/campaigns/${submissionSlug}/enter`,
        formDataToSend,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      // Clear simulated timeouts since we have real validation steps now
      if (step1Timeout) clearTimeout(step1Timeout);
      if (step2Timeout) clearTimeout(step2Timeout);
      
      // Entry accepted by the server (validation failures return 4xx and land in the catch below)
      trackMetaEvent('CompleteRegistration', campaign?.meta_pixel_id);
      trackTikTokEvent('CompleteRegistration', campaign?.tiktok_pixel_id);

      setEntryNumber(response.data.entryNumber);
      setEntrySequence(response.data.entrySequence ?? null);
      setEntryStatus(response.data.status);

      // Update validation steps from backend response if available
      if (response.data.validationSteps) {
        setValidationSteps(response.data.validationSteps);
      }
      
      // Store OCR metadata if available (for display/debugging)
      const methodFromResponse = response.data.parsingMethod || response.data.ocrMetadata?.method || null;
      const structuredReceiptData = response.data.structuredReceipt || response.data.ocrMetadata?.structuredReceipt || null;

      if (response.data.ocrMetadata || response.data.confidence || structuredReceiptData || methodFromResponse) {
        setOcrMetadata({
          confidence: response.data.confidence || response.data.ocrMetadata?.confidence,
          extractedText: response.data.extractedText || response.data.ocrMetadata?.extractedText,
          textLength: response.data.textLength || response.data.ocrMetadata?.textLength,
          method: methodFromResponse,
          structuredReceipt: structuredReceiptData
        });
      } else {
        setOcrMetadata(null);
      }

      setParsingMethod(methodFromResponse);
      setStructuredReceipt(structuredReceiptData || null);
      
      // Check for cashback voucher (guaranteed reward, no draw needed)
      if (response.data.cashbackVoucher) {
        setCashbackVoucher(response.data.cashbackVoucher);
        setGiftCard(null);
      } else if (response.data.giftCard) {
        setGiftCard(response.data.giftCard);
        setCashbackVoucher(null);
      } else {
        setCashbackVoucher(null);
        setGiftCard(null);
      }

      // Don't set validation messages - we'll show a generic success message for all entries
      // Clear any existing validation message
      setValidationMessage('');

      // Cashback campaigns get their voucher immediately — skip instant win flow (type from mechanics_types table)
      const isCashbackCampaign = (campaign?.mechanics_type_type || '').toLowerCase() === 'cashback';
      const isMembershipCampaign = (campaign?.mechanics_type_type || '').toLowerCase() === 'membership';

      if (isCashbackCampaign) {
        setInstantWinResult({ eligible: false, won: false });
        setShowThankYou(true);

        const resetFormData = {};
        allFields.forEach(field => {
          if (field.field_type === 'checkbox' && !field.is_system) {
            resetFormData[field.field_name] = [];
          } else {
            resetFormData[field.field_name] = '';
          }
        });
        resetFormData.agree_terms = false;
        resetFormData.marketing_consent = true;
        setFormData(resetFormData);
        setReceiptFile(null);
        resetVideoRecordingState();
        setValidationSteps(null);
        setShowValidationProgress(false);
        setOcrMetadata(null);
      } else if (isMembershipCampaign) {
        // Membership: card/email sent by backend after form submit — no validation needed, show thank you immediately
        setInstantWinResult({ eligible: false, won: false });
        setShowThankYou(true);

        const resetFormData = {};
        allFields.forEach(field => {
          if (field.field_type === 'checkbox' && !field.is_system) {
            resetFormData[field.field_name] = [];
          } else {
            resetFormData[field.field_name] = '';
          }
        });
        resetFormData.agree_terms = false;
        resetFormData.marketing_consent = true;
        setFormData(resetFormData);
        setReceiptFile(null);
        resetVideoRecordingState();
        setValidationSteps(null);
        setShowValidationProgress(false);
        setOcrMetadata(null);
      } else {
        // Check for instant win result
        if (response.data.instantWin) {
          setInstantWinResult(response.data.instantWin);
        } else {
          setInstantWinResult({ eligible: false, won: false });
        }
        // Set flag to wait for instant win result before showing thank you page
        setWaitingForInstantWin(true);
      }
      // Don't reset form here - wait until after thank you page is shown
    } catch (err) {
      // Clear simulated timeouts since we have error response
      if (step1Timeout) clearTimeout(step1Timeout);
      if (step2Timeout) clearTimeout(step2Timeout);
      
      // Capture validation steps if available
      if (err.response?.data?.validationSteps) {
        setValidationSteps(err.response.data.validationSteps);
      }
      
      // Capture OCR metadata if available
      const errorParsingMethod = err.response?.data?.parsingMethod || err.response?.data?.ocrMetadata?.method || null;
      const errorStructuredReceipt = err.response?.data?.structuredReceipt || err.response?.data?.ocrMetadata?.structuredReceipt || null;

      if (err.response?.data?.ocrMetadata || err.response?.data?.confidence || errorStructuredReceipt || errorParsingMethod) {
        setOcrMetadata({
          confidence: err.response?.data?.confidence || err.response?.data?.ocrMetadata?.confidence,
          extractedText: err.response?.data?.extractedText || err.response?.data?.ocrMetadata?.extractedText,
          textLength: err.response?.data?.textLength || err.response?.data?.ocrMetadata?.textLength,
          method: errorParsingMethod,
          structuredReceipt: errorStructuredReceipt
        });
      }

      if (errorParsingMethod) {
        setParsingMethod(errorParsingMethod);
      }

      if (errorStructuredReceipt) {
        setStructuredReceipt(errorStructuredReceipt);
      }
      
      // Handle 400 Bad Request specifically
      if (err.response?.status === 400) {
        // Check if it's a pack size mismatch error by looking at validation steps
        const errorMessage = err.response?.data?.error || '';
        const responseValidationSteps = err.response?.data?.validationSteps;
        
        // Check if validation steps indicate pack size mismatch or product match failure
        const isPackSizeMismatch = responseValidationSteps && 
          responseValidationSteps.step2 && 
          responseValidationSteps.step2.message && 
          responseValidationSteps.step2.message.includes('Pack size mismatch');
        
        const isProductMatchFailure = responseValidationSteps && 
          responseValidationSteps.step2 && 
          responseValidationSteps.step2.status === 'failed';

        const isStep1Failed = responseValidationSteps?.step1?.status === 'failed';
        const isReceiptExtractionFailure =
          errorMessage.includes('Could not extract valid product') ||
          errorMessage.includes('Failed to extract receipt data') ||
          errorMessage.includes('We could not read product lines from this receipt');
        
        // Set validation steps from response if available
        if (responseValidationSteps) {
          setValidationSteps(responseValidationSteps);
        }
        
        // For validation failures (product match, pack size mismatch, OCR/extraction, etc.), show the error but keep validation steps visible
        if (isPackSizeMismatch || isProductMatchFailure || isStep1Failed || isReceiptExtractionFailure || (errorMessage.includes('pack') && errorMessage.includes('mismatch')) || errorMessage.includes('No product match found') || errorMessage.includes('Receipt does not contain')) {
          // Show error message but keep validation steps visible so user can see what failed
          if (errorMessage) {
            setFormErrors({ submit: errorMessage });
          }
          // Keep validation progress visible so user can see what failed
          setShowValidationProgress(true);
          // Don't clear validation steps - let user see what failed
          return; // Exit early - don't proceed to finally block that would hide progress
        } else {
          if (errorMessage) {
            setFormErrors({ submit: errorMessage });
          } else {
            setFormErrors({ submit: 'Bad Request: Please check your entry details and try again. Make sure all required fields are filled correctly.' });
          }

          setShowValidationProgress(false);
          setValidationSteps(null);
        }

        // Handle entry limit messaging explicitly (still within 400 errors)
        if (!isPackSizeMismatch && errorMessage) {
          const entryLimitReached = isEntryLimitError(errorMessage);
          const receiptImageIssue = isReceiptImageQualityError(errorMessage);

          if (entryLimitReached) {
            setValidationSteps(null);
            setShowValidationProgress(false);
          }

          if (entryLimitReached || receiptImageIssue) {
            setTimeout(() => {
              const errorElement = document.querySelector('.bg-yellow-50.border-yellow-400');
              if (errorElement) {
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 100);
          }
        }
        
        // For pack size mismatches, keep validation steps visible (don't clear them)
        // The validation steps will show the pack size mismatch error from the backend
        
        return; // Exit early for 400 errors
      }
      
      if (err.response?.data?.errors) {
        setShowValidationProgress(false);
        setValidationSteps(null);
        // Handle validation errors
        const errors = {};
        err.response.data.errors.forEach(error => {
          errors[error.path || error.param || 'submit'] = error.msg;
        });
        setFormErrors(errors);
      } else if (err.response?.data?.error) {
        const errorMessage = err.response.data.error;
        setFormErrors({ submit: errorMessage });
        
        // Check if it's a max entries error
        if (isEntryLimitError(errorMessage)) {
          setValidationSteps(null);
          setShowValidationProgress(false);

          // Scroll to the error message area (near the submit button)
          setTimeout(() => {
            const errorElement = document.querySelector('.bg-yellow-50.border-yellow-400');
            if (errorElement) {
              errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        } else {
          setShowValidationProgress(false);
          setValidationSteps(null);
        }
      } else {
        setShowValidationProgress(false);
        setValidationSteps(null);
        setFormErrors({ submit: 'Failed to submit entry. Please try again.' });
      }
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  // Contact form handler
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    const errors = {};
    if (!contactFormData.full_name.trim()) {
      errors.full_name = 'Full name is required';
    }
    if (!contactFormData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(contactFormData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!contactFormData.message.trim()) {
      errors.message = 'Message is required';
    }
    
    setContactFormErrors(errors);
    if (Object.keys(errors).length > 0) return;
    
    setContactSubmitting(true);
    
    try {
      await api.post(`/public/campaigns/${resolvedSlug}/contact`, {
        ...contactFormData,
        campaign_id: campaign?.id
      });
      
      setContactFormSuccess(true);
      setContactFormData({
        full_name: '',
        email: '',
        message: ''
      });
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setContactFormSuccess(false);
      }, 5000);
      
    } catch (err) {
      if (err.response?.data?.error) {
        setContactFormErrors({ submit: err.response.data.error });
      } else {
        setContactFormErrors({ submit: 'Failed to send message. Please try again.' });
      }
    } finally {
      setContactSubmitting(false);
    }
  };

  // Helper function to render a specific section instance
  const renderSectionInstance = (section, sectionIndex = 0) => {
    const uniqueKey = `${section.type}-${section.id || sectionIndex}`;
    
    switch (section.type) {
      case 'banner':
        return (
          <div key={uniqueKey} className="pb-6">
            <div className="container mx-auto px-4">
              {section.background_image_url ? (
                section.background_image_url.match(/\.(mp4|webm|ogg)$/i) ? (
                  // Video background
                  <div className="relative w-full aspect-[2933/1382] rounded-lg shadow-lg overflow-hidden">
                    <video
                      className="absolute inset-0 w-full h-full object-cover"
                      autoPlay
                      loop
                      muted
                      playsInline
                    >
                      <source src={`${ASSET_BASE_URL}${section.background_image_url}`} />
                      Your browser does not support the video tag.
                    </video>
                    <div className="absolute inset-0 bg-black bg-opacity-20"></div>
                  </div>
                ) : (
                  // Image background
                  <div 
                    className={`relative w-full aspect-[2933/1382] bg-cover bg-center bg-no-repeat rounded-lg shadow-lg overflow-hidden ${
                      section.full_width_background ? 'w-full' : ''
                    }`}
                    style={{
                      backgroundColor: campaign?.primary_color || '#1F2937',
                      backgroundImage: `url(${ASSET_BASE_URL}${section.background_image_url})`
                    }}
                  >
                    <div className="absolute inset-0 bg-black bg-opacity-20"></div>
                  </div>
                )
              ) : (
                <div 
                  className="h-32 rounded-lg shadow-lg"
                  style={{ backgroundColor: campaign?.primary_color || '#1F2937' }}
                ></div>
              )}
            </div>
          </div>
        );

      case 'content':
        // Check template codename to determine layout
        if (section.template_codename === 'content-with-background-overlay') {
          // Content with Background Overlay template - Container width with overlay
          return (
            <section key={uniqueKey} className="pb-6 content-with-background-overlay">
              <div className="container mx-auto px-4">
                {section.background_image_url ? (
                  <>
                    {section.background_image_url.match(/\.(mp4|webm|ogg)$/i) ? (
                      // Video background with overlay
                      <div className="relative min-h-[500px] md:min-h-[600px] lg:min-h-[700px] rounded-lg shadow-lg overflow-hidden">
                        <video
                          className="absolute inset-0 w-full h-full object-cover"
                          autoPlay
                          loop
                          muted
                          playsInline
                        >
                          <source src={`${ASSET_BASE_URL}${section.background_image_url}`} />
                          Your browser does not support the video tag.
                        </video>
                        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
                        <div className="relative h-full min-h-[500px] md:min-h-[600px] lg:min-h-[700px] flex items-center justify-center p-8 md:p-12 lg:p-16">
                          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10 lg:p-12 w-full max-w-3xl mx-auto animate-fadeInUp">
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-center leading-relaxed" style={{ color: campaign?.primary_color || '#1F2937' }}>
                              {section.about_header || 'About This Campaign'}
                            </h2>
                            <div className="prose prose-lg mx-auto">
                              <div 
                                className="text-base md:text-lg lg:text-xl leading-relaxed content-alignment-wrapper" 
                                dangerouslySetInnerHTML={{
                                  __html: section.about_description || campaign?.description || renderMechanicsDescription()
                                }} 
                              />
                            </div>
                            {renderSectionYouTubeVideo(section, {
                              wrapperClass: 'mt-8',
                              roundedClass: 'rounded-xl',
                              shadowClass: 'shadow-md'
                            })}
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Image background with overlay
                      <div 
                        className="relative min-h-[500px] md:min-h-[600px] lg:min-h-[700px] rounded-lg shadow-lg overflow-hidden bg-cover bg-center bg-no-repeat"
                        style={{
                          backgroundImage: `url(${ASSET_BASE_URL}${section.background_image_url})`
                        }}
                      >
                        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
                        <div className="relative h-full min-h-[500px] md:min-h-[600px] lg:min-h-[700px] flex items-center justify-center p-8 md:p-12 lg:p-16">
                          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10 lg:p-12 w-full max-w-3xl mx-auto animate-fadeInUp">
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-center leading-relaxed" style={{ color: campaign?.primary_color || '#1F2937' }}>
                              {section.about_header || 'About This Campaign'}
                            </h2>
                            <div className="prose prose-lg mx-auto">
                              <div 
                                className="text-base md:text-lg lg:text-xl leading-relaxed content-alignment-wrapper" 
                                dangerouslySetInnerHTML={{
                                  __html: section.about_description || campaign?.description || renderMechanicsDescription()
                                }} 
                              />
                            </div>
                            {renderSectionYouTubeVideo(section, {
                              wrapperClass: 'mt-8',
                              roundedClass: 'rounded-xl',
                              shadowClass: 'shadow-md'
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  // Fallback if no background image
                  <div className="bg-gray-900 rounded-lg shadow-lg p-16">
                    <div className="text-center leading-relaxed max-w-4xl mx-auto">
                      <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 text-white">
                        {section.about_header || 'About This Campaign'}
                      </h2>
                      <div className="prose prose-xl prose-invert mx-auto">
                        <div className="text-xl md:text-2xl lg:text-3xl text-white" dangerouslySetInnerHTML={{
                          __html: section.about_description || campaign?.description || renderMechanicsDescription()
                        }} />
                      </div>
                      {renderSectionYouTubeVideo(section, {
                        wrapperClass: 'mt-10 max-w-4xl mx-auto',
                        roundedClass: 'rounded-xl',
                        shadowClass: 'shadow-2xl',
                        innerClass: 'border border-white/20'
                      })}
                    </div>
                  </div>
                )}
              </div>
            </section>
          );
        } else if (!section.about_with_form && section.background_image_url) {
          // Content with Background template (full-width background)
          return (
            <section key={uniqueKey} className="relative py-0">
              {/* Full-width background */}
              {section.background_image_url.match(/\.(mp4|webm|ogg)$/i) ? (
                // Video background
                <div className="relative min-h-[600px] md:min-h-[700px]">
                  <video
                    className="absolute inset-0 w-full h-full object-cover"
                    autoPlay
                    loop
                    muted
                    playsInline
                  >
                    <source src={`${ASSET_BASE_URL}${section.background_image_url}`} />
                    Your browser does not support the video tag.
                  </video>
                  <div className="absolute inset-0 bg-black bg-opacity-40"></div>
                  {/* Content overlay */}
                  <div className="relative z-10 flex items-center min-h-[600px] md:min-h-[700px]">
                    <div className="container mx-auto px-4 py-20">
                      <div className="max-w-3xl mx-auto text-center leading-relaxed animate-fadeInUp">
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white drop-shadow-lg">
                          {section.about_header || 'About This Campaign'}
                        </h2>
                        <div className="prose prose-xl prose-invert mx-auto">
                          <div className="text-lg md:text-xl lg:text-2xl text-white drop-shadow" dangerouslySetInnerHTML={{
                            __html: section.about_description || campaign?.description || renderMechanicsDescription()
                          }} />
                        </div>
                      {renderSectionYouTubeVideo(section, {
                        wrapperClass: 'mt-8',
                        roundedClass: 'rounded-xl',
                        shadowClass: 'shadow-xl'
                      })}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // Image background
                <div 
                  className="relative min-h-[600px] md:min-h-[700px] bg-cover bg-center bg-no-repeat bg-fixed"
                  style={{
                    backgroundImage: `url(${ASSET_BASE_URL}${section.background_image_url})`
                  }}
                >
                  <div className="absolute inset-0 bg-black bg-opacity-40"></div>
                  {/* Content overlay */}
                  <div className="relative z-10 flex items-center min-h-[600px] md:min-h-[700px]">
                    <div className="container mx-auto px-4 py-20">
                      <div className="max-w-3xl mx-auto text-center leading-relaxed animate-fadeInUp">
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white drop-shadow-lg">
                          {section.about_header || 'About This Campaign'}
                        </h2>
                        <div className="prose prose-xl prose-invert mx-auto">
                          <div className="text-lg md:text-xl lg:text-2xl text-white drop-shadow" dangerouslySetInnerHTML={{
                            __html: section.about_description || campaign?.description || renderMechanicsDescription()
                          }} />
                        </div>
                      {renderSectionYouTubeVideo(section, {
                        wrapperClass: 'mt-8',
                        roundedClass: 'rounded-xl',
                        shadowClass: 'shadow-xl'
                      })}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </section>
          );
        } else if (section.template_codename === 'content-without-background') {
          return (
            <section key={uniqueKey} className="py-12">
              <div className="container mx-auto px-4">
                <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 lg:p-16 text-center leading-relaxed w-full">
                  {section.about_header && (
                    <h2
                      className="text-4xl md:text-5xl font-bold mb-6"
                      style={{ color: campaign?.primary_color || '#1F2937' }}
                    >
                      {section.about_header}
                    </h2>
                  )}
                  {(section.about_description || campaign?.description) && (
                    <div className="prose prose-lg mx-auto">
                      <div
                        className="text-base md:text-lg text-gray-700 leading-relaxed"
                        dangerouslySetInnerHTML={{
                          __html: section.about_description || campaign?.description || ''
                        }}
                      />
                    </div>
                  )}
                  {renderSectionYouTubeVideo(section, {
                    wrapperClass: 'mt-8 max-w-3xl mx-auto',
                    roundedClass: 'rounded-2xl',
                    shadowClass: 'shadow-lg'
                  })}
                </div>
              </div>
            </section>
          );
        } else {
          // Content with Forms template (default)
          return (
            <section key={uniqueKey} className="py-12">
              <div className="container mx-auto px-4">
                <div className={`grid grid-cols-1 ${section.about_with_form && form ? 'lg:grid-cols-2' : 'lg:grid-cols-1'} gap-12`}>
                  {/* About Info */}
                  <div className="animate-fadeInUp">
                    <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: campaign?.primary_color || '#1F2937' }}>
                      {section.about_header || 'About This Campaign'}
                    </h2>
                    <div className="prose prose-xl">
                      <div className="text-lg md:text-xl leading-relaxed" dangerouslySetInnerHTML={{
                        __html: section.about_description || campaign?.description || renderMechanicsDescription()
                      }} />
                    </div>
                    {renderSectionYouTubeVideo(section, {
                      wrapperClass: 'mt-6',
                      roundedClass: 'rounded-xl',
                      shadowClass: 'shadow-lg'
                    })}
                  </div>

                  {/* Entry Form - Only show if a form exists and about_with_form is true */}
                  {section.about_with_form && form ? renderEntryForm() : null}
                </div>
              </div>
            </section>
          );
        }

      case 'forms':
        // Standalone form section
        if (!form) return null;
        
        // Check template for forms
        if (section.template_codename === 'forms-with-background' && section.background_image_url) {
          // Forms with Background template - split screen with image on left
          return (
            <section key={uniqueKey} className="py-16">
              <div className="container mx-auto px-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
                  {/* Left Column: Background Image */}
                  <div className="relative h-64 lg:h-full">
                    <div className="h-full overflow-hidden rounded-2xl shadow-lg">
                      {section.background_image_url.match(/\.(mp4|webm|ogg)$/i) ? (
                        // Video background
                        <video
                          className="w-full h-full object-cover"
                          autoPlay
                          loop
                          muted
                          playsInline
                        >
                          <source src={`${ASSET_BASE_URL}${section.background_image_url}`} />
                          Your browser does not support the video tag.
                        </video>
                      ) : (
                        // Image background
                        <img
                          src={`${ASSET_BASE_URL}${section.background_image_url}`}
                          alt="Form background"
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                  </div>

                  {/* Right Column: Form */}
                  <div className="w-full">
                    {renderEntryForm()}
                  </div>
                </div>
              </div>
            </section>
          );
        } else {
          // Forms Normal template - centered form
          return (
            <section key={uniqueKey} className="py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-2xl mx-auto">
                  {renderEntryForm()}
                </div>
              </div>
            </section>
          );
        }

      case 'faqs':
        const sectionFaqs = section.faqs || [];
        
        // Check template to determine layout
        if (section.template_codename === 'faqs-v2') {
          // FAQs V2 - Centered layout
          return (
            <section key={uniqueKey} className="py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  {/* Centered Header & Description */}
                  <div className="text-center leading-relaxed mb-12 animate-fadeInUp">
                    <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: campaign?.primary_color || '#1F2937' }}>
                      {section.faqs_header || 'Frequently Asked Questions'}
                    </h2>
                    {section.faqs_description && (
                      <div className="prose prose-xl mx-auto">
                        <div className="text-lg md:text-xl text-gray-600" dangerouslySetInnerHTML={{
                          __html: section.faqs_description
                        }} />
                      </div>
                    )}
                  </div>
                  
                  {/* FAQs List */}
                  {sectionFaqs.length > 0 ? (
                    <div className="space-y-4 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
                      {sectionFaqs.map((faq) => (
                        <details key={faq.id} className="border border-gray-200 rounded-lg p-6 hover:border-gray-300 transition-colors">
                          <summary className="font-semibold text-xl cursor-pointer flex items-center justify-between">
                            {faq.question}
                          </summary>
                          <div
                            className="mt-4 faq-answer text-lg"
                            dangerouslySetInnerHTML={{ __html: faq.answer }}
                          />
                        </details>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-600 text-center leading-relaxed mt-6">No FAQs available at this time.</p>
                  )}
                </div>
              </div>
            </section>
          );
        } else if (section.template_codename === 'faqs-with-accent-color') {
          // FAQs with Accent Color - Full-width background
          return (
            <section key={uniqueKey} className="py-16" style={{ backgroundColor: campaign?.accent_color || '#047857' }}>
              <div className="container mx-auto px-4">
                <div className="max-w-5xl mx-auto">
                  {/* Header & Description */}
                  <div className="mb-12 animate-fadeInUp">
                    <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                      {section.faqs_header || 'Any questions? We got you.'}
                    </h2>
                    {section.faqs_description && (
                      <div className="prose prose-xl prose-invert max-w-none">
                        <div className="text-lg md:text-xl text-white opacity-90" dangerouslySetInnerHTML={{
                          __html: section.faqs_description
                        }} />
                      </div>
                    )}
                  </div>
                  
                  {/* FAQs List with custom styling */}
                  {sectionFaqs.length > 0 ? (
                    <div className="space-y-0 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
                      {sectionFaqs.map((faq) => (
                        <details key={faq.id} className="border-t border-white/20 group">
                          <summary className="py-6 cursor-pointer block">
                            <div className="flex items-center justify-between w-full">
                              <h3 className="text-xl font-medium text-white pr-4">{faq.question}</h3>
                            </div>
                          </summary>
                          <div className="pb-6 -mt-2">
                            <div className="faq-answer text-lg text-white opacity-80" dangerouslySetInnerHTML={{ __html: faq.answer }} />
                          </div>
                        </details>
                      ))}
                      <div className="border-t border-white/20"></div>
                    </div>
                  ) : (
                    <p className="text-white/80 text-center leading-relaxed mt-6">No FAQs available at this time.</p>
                  )}
                </div>
              </div>
            </section>
          );
        } else {
          // Default FAQs template - Grid layout
          return sectionFaqs.length > 0 ? (
            <section key={uniqueKey} className="py-16">
              <div className="container mx-auto px-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                  {/* FAQ Header & Description */}
                  <div className="animate-fadeInUp">
                    <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: campaign?.primary_color || '#1F2937' }}>
                      {section.faqs_header || 'Frequently Asked Questions'}
                    </h2>
                    {section.faqs_description && (
                      <div className="prose prose-xl">
                        <div className="text-lg md:text-xl" dangerouslySetInnerHTML={{
                          __html: section.faqs_description
                        }} />
                      </div>
                    )}
                  </div>
                  
                  {/* FAQs List - Use section-specific FAQs */}
                  <div className="space-y-4 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
                    {sectionFaqs.map((faq) => (
                      <details key={faq.id} className="border border-gray-200 rounded-lg p-6 hover:border-gray-300 transition-colors">
                        <summary className="font-semibold text-xl cursor-pointer flex items-center justify-between">
                          {faq.question}
                        </summary>
                        <div className="mt-4 faq-answer text-lg" dangerouslySetInnerHTML={{ __html: faq.answer }} />
                      </details>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          ) : (
            // Show section even if no FAQs, just with header/description
            <section key={uniqueKey} className="py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto text-center leading-relaxed">
                  <div className="animate-fadeInUp">
                    <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: campaign?.primary_color || '#1F2937' }}>
                      {section.faqs_header || 'Frequently Asked Questions'}
                    </h2>
                    {section.faqs_description && (
                      <div className="prose prose-xl mx-auto">
                        <div className="text-lg md:text-xl" dangerouslySetInnerHTML={{
                          __html: section.faqs_description
                        }} />
                      </div>
                    )}
                    <p className="text-gray-600 mt-6">No FAQs available at this time.</p>
                  </div>
                </div>
              </div>
            </section>
          );
        }

      case 'gallery':
        const sectionGallery = section.gallery || [];
        return sectionGallery.length > 0 ? (
          <section key={uniqueKey} className="py-16">
            <div className="container mx-auto px-4">
              <div className={sectionGallery.length < 4 
                ? "flex flex-wrap justify-center gap-6" 
                : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"}>
                {sectionGallery.map((image, index) => (
                  <div key={image.id} className={`relative group overflow-hidden rounded-lg shadow-lg ${
                    sectionGallery.length < 4 ? "w-full max-w-sm" : ""
                  }`}>
                    <img
                      src={`${ASSET_BASE_URL}${image.image_url}`}
                      alt={image.title || `Gallery image ${index + 1}`}
                      className="w-full h-80 md:h-96 object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    {(image.title || image.description) && (
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                        <div className="text-white p-4 w-full">
                          {image.title && <h3 className="font-bold text-lg mb-1">{image.title}</h3>}
                          {image.description && <p className="text-sm opacity-90">{image.description}</p>}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        ) : null;


      default:
        return null;
    }
  };

  // Legacy function for backward compatibility when no sections exist
  const renderLegacySection = (sectionId) => {
    switch (sectionId) {
      case 'banner':
        return (
          <div key="banner" className="pb-6">
            <div className="container mx-auto px-4">
              {campaign?.background_image_url ? (
                campaign.background_image_url.match(/\.(mp4|webm|ogg)$/i) ? (
                  // Video background
                  <div className="relative w-full aspect-[2933/1382] rounded-lg shadow-lg overflow-hidden">
                    <video
                      className="absolute inset-0 w-full h-full object-cover"
                      autoPlay
                      loop
                      muted
                      playsInline
                    >
                      <source src={`${ASSET_BASE_URL}${campaign.background_image_url}`} />
                      Your browser does not support the video tag.
                    </video>
                    <div className="absolute inset-0 bg-black bg-opacity-20"></div>
                  </div>
                ) : (
                  // Image background
                  <div 
                    className="relative w-full aspect-[2933/1382] bg-cover bg-center bg-no-repeat rounded-lg shadow-lg overflow-hidden"
                    style={{
                      backgroundColor: campaign?.primary_color || '#1F2937',
                      backgroundImage: `url(${ASSET_BASE_URL}${campaign.background_image_url})`
                    }}
                  >
                    <div className="absolute inset-0 bg-black bg-opacity-20"></div>
                  </div>
                )
              ) : (
                <div 
                  className="h-32 rounded-lg shadow-lg"
                  style={{ backgroundColor: campaign?.primary_color || '#1F2937' }}
                ></div>
              )}
            </div>
          </div>
        );

      case 'content':
        return (
          <section key="content" className="py-12">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* About Info */}
                <div className="animate-fadeInUp">
                  <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: campaign?.primary_color || '#1F2937' }}>
                    {campaign?.about_header || 'About This Campaign'}
                  </h2>
                  <div className="prose prose-xl">
                    <p className="whitespace-pre-wrap text-lg md:text-xl">
                      {campaign?.about_description || campaign?.description || renderMechanicsDescription()}
                    </p>
                  </div>
                </div>

                {/* Entry Form - Only show if a form exists */}
                {form ? renderEntryForm() : null}
              </div>
            </div>
          </section>
        );

      case 'faqs':
        return faqs.length > 0 ? (
          <section key="faqs" className="py-16">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* FAQ Header & Description */}
                <div className="animate-fadeInUp">
                  <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: campaign?.primary_color || '#1F2937' }}>
                    {campaign?.faq_header || 'Frequently Asked Questions'}
                  </h2>
                  {campaign?.faq_description && (
                    <div className="prose prose-xl">
                      <p className="whitespace-pre-wrap text-lg md:text-xl">
                        {campaign.faq_description}
                      </p>
                    </div>
                  )}
                </div>
                
                {/* FAQs List */}
                <div className="space-y-4 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
                  {faqs.map((faq) => (
                    <details key={faq.id} className="border border-gray-200 rounded-lg p-6 hover:border-gray-300 transition-colors">
                      <summary className="font-semibold text-xl cursor-pointer flex items-center justify-between">
                        {faq.question}
                      </summary>
                      <div className="mt-4 faq-answer text-lg" dangerouslySetInnerHTML={{ __html: faq.answer }} />
                    </details>
                  ))}
                </div>
              </div>
            </div>
          </section>
        ) : null;

      case 'gallery':
        return gallery.length > 0 ? (
          <section key="gallery" className="py-16">
            <div className="container mx-auto px-4">
              <div className={gallery.length < 4 
                ? "flex flex-wrap justify-center gap-6" 
                : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"}>
                {gallery.map((image, index) => (
                  <div key={image.id} className={`relative group overflow-hidden rounded-lg shadow-lg ${
                    gallery.length < 4 ? "w-full max-w-sm" : ""
                  }`}>
                    <img
                      src={`${ASSET_BASE_URL}${image.image_url}`}
                      alt={image.title || `Gallery image ${index + 1}`}
                      className="w-full h-80 md:h-96 object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    {(image.title || image.description) && (
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                        <div className="text-white p-6 w-full">
                          {image.title && <h3 className="font-bold text-lg mb-1">{image.title}</h3>}
                          {image.description && <p className="text-sm opacity-90">{image.description}</p>}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        ) : null;

      case 'contact':
        return (
          <section key="contact" className="py-20" style={{ backgroundColor: campaign?.accent_color || '#FFD100' }}>
            <div className="container mx-auto px-4">
              <div className="text-center leading-relaxed mb-16">
                <h2 className="text-5xl font-bold mb-4" style={{ color: campaign?.secondary_color || '#002F87' }}>
                  Get in Touch
                </h2>
                <p className="text-xl max-w-2xl mx-auto" style={{ color: campaign?.secondary_color || '#002F87' }}>
                  Have questions or feedback? We'd love to hear from you.
                </p>
              </div>
              
              <div className="max-w-2xl mx-auto">
                {/* Contact Form */}
                <div className="bg-white rounded-3xl shadow-2xl p-10 border border-gray-100 relative overflow-hidden">
                  <div className="relative z-10">
                    {contactFormSuccess && (
                      <div className="mb-8 p-6 rounded-2xl border" style={{ 
                        background: `linear-gradient(135deg, ${campaign?.secondary_color || '#f0fdf4'}, ${campaign?.accent_color || '#dbeafe'}20)`, 
                        borderColor: campaign?.accent_color || '#2563eb' 
                      }}>
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3" style={{ backgroundColor: campaign?.accent_color || '#2563eb' }}>
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold" style={{ color: campaign?.primary_color || '#1F2937' }}>Message Sent!</h3>
                            <p style={{ color: campaign?.primary_color || '#1F2937' }}>Thank you for reaching out. We'll get back to you soon.</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <form onSubmit={handleContactSubmit} className="space-y-6">
                      <div className="space-y-2">
                        <label htmlFor="legacy_full_name" className="block text-sm font-semibold mb-2" style={{ color: campaign?.primary_color || '#002F87' }}>
                          Full Name
                        </label>
                        <input
                          type="text"
                          id="legacy_full_name"
                          value={contactFormData.full_name}
                          onChange={(e) => setContactFormData(prev => ({ ...prev, full_name: e.target.value }))}
                          className={`w-full px-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:bg-white transition-all duration-200 hover:border-gray-300 ${
                            contactFormErrors.full_name ? 'border-red-500 focus:border-red-500' : ''
                          }`}
                          style={{
                            borderColor: contactFormErrors.full_name ? '#ef4444' : '#e5e7eb',
                            '--tw-ring-color': campaign?.accent_color || '#2563eb'
                          }}
                          placeholder="Enter your full name"
                          required
                        />
                        {contactFormErrors.full_name && (
                          <p className="text-red-500 text-sm mt-1">{contactFormErrors.full_name}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <label htmlFor="legacy_email" className="block text-sm font-semibold mb-2" style={{ color: campaign?.primary_color || '#002F87' }}>
                          Email Address
                        </label>
                        <input
                          type="email"
                          id="legacy_email"
                          value={contactFormData.email}
                          onChange={(e) => setContactFormData(prev => ({ ...prev, email: e.target.value }))}
                          className={`w-full px-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:bg-white transition-all duration-200 hover:border-gray-300 ${
                            contactFormErrors.email ? 'border-red-500 focus:border-red-500' : ''
                          }`}
                          style={{
                            borderColor: contactFormErrors.email ? '#ef4444' : '#e5e7eb',
                            '--tw-ring-color': campaign?.accent_color || '#2563eb'
                          }}
                          placeholder="Enter your email address"
                          required
                        />
                        {contactFormErrors.email && (
                          <p className="text-red-500 text-sm mt-1">{contactFormErrors.email}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <label htmlFor="legacy_message" className="block text-sm font-semibold mb-2" style={{ color: campaign?.primary_color || '#002F87' }}>
                          Message
                        </label>
                        <textarea
                          id="legacy_message"
                          rows={5}
                          value={contactFormData.message}
                          onChange={(e) => setContactFormData(prev => ({ ...prev, message: e.target.value }))}
                          className={`w-full px-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:bg-white transition-all duration-200 hover:border-gray-300 resize-none ${
                            contactFormErrors.message ? 'border-red-500 focus:border-red-500' : ''
                          }`}
                          style={{
                            borderColor: contactFormErrors.message ? '#ef4444' : '#e5e7eb',
                            '--tw-ring-color': campaign?.accent_color || '#2563eb'
                          }}
                          placeholder="Tell us how we can help you..."
                          required
                        />
                        {contactFormErrors.message && (
                          <p className="text-red-500 text-sm mt-1">{contactFormErrors.message}</p>
                        )}
                      </div>

                      {contactFormErrors.submit && (
                        <div className="p-4 rounded-lg border border-red-400" style={{ backgroundColor: '#fef2f2' }}>
                          <p className="text-red-700">{contactFormErrors.submit}</p>
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={contactSubmitting}
                        className="w-full py-4 px-8 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-lg"
                        style={{ 
                          backgroundColor: contactSubmitting ? '#9ca3af' : campaign?.primary_color || '#002F87',
                          '--tw-ring-color': campaign?.primary_color || '#002F87'
                        }}
                      >
                        {contactSubmitting ? (
                          <div className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Sending Message...
                          </div>
                        ) : (
                          <div className="flex items-center justify-center">
                            Send Message
                          </div>
                        )}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </section>
        );

      case 'terms-footer':
        return campaign?.terms_and_conditions ? (
          <footer className="bg-gray-900 text-white py-8">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                  {/* Left side - Campaign info */}
                  <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6">
                    <div className="flex items-center space-x-2">
                      {campaign?.logo_url && (
                        <img 
                          src={`${ASSET_BASE_URL}${campaign.logo_url}`} 
                          alt={campaign.name}
                          className="h-8 w-auto"
                        />
                      )}
                      <span className="text-lg font-semibold">{campaign?.name}</span>
                    </div>
                    <div className="text-sm text-gray-400">
                      © {new Date().getFullYear()} All rights reserved
                    </div>
                  </div>
                  
                  {/* Right side - Terms and Privacy */}
                  <div className="flex items-center space-x-6">
                    <button
                      onClick={() => setShowTermsModal(true)}
                      className="text-sm text-gray-300 hover:text-white transition-colors underline"
                    >
                      Terms & Conditions
                    </button>
                    <a
                      href="https://gooddrinks.com.au/privacy-policy/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-gray-300 hover:text-white transition-colors underline"
                    >
                      Privacy Policy
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </footer>
        ) : null;

      default:
        return null;
    }
  };

  const renderEntryForm = () => {
    return (
      <div className="animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
        <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: campaign?.primary_color || '#1F2937' }}>
          {(form?.form_headline && String(form.form_headline).trim()) || 'Enter to Win!'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* All Form Fields in Order */}
          {allFields.map(field => isFieldVisible(field) ? renderField(field) : null)}

          {!!campaign?.requires_receipt && (
            <div>
              <label className="block text-base font-medium mb-2">
                {getReceiptUploadLabel(form)} <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-indigo-400 transition-colors">
                <div className="space-y-1 text-center leading-relaxed">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div className="flex text-sm text-gray-600">
                    <label className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500">
                      <span>Upload a file</span>
                      <input
                        type="file"
                        onChange={handleFileChange}
                        accept=".jpg,.jpeg,.png,.pdf"
                        className="sr-only"
                        required
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">
                    JPG, PNG, PDF up to 10MB
                  </p>
                  {receiptFile && (
                    <p className="text-sm text-green-600 mt-2">
                      ✓ {receiptFile.name}
                    </p>
                  )}
                </div>
              </div>
              {formErrors.receipt && (
                <p className="mt-1 text-sm text-red-600">{formErrors.receipt}</p>
              )}
            </div>
          )}

          <div className="bg-gray-50 p-4 rounded-md space-y-3">
            <div className="flex items-start">
              <input
                type="checkbox"
                name="agree_terms"
                id="agree_terms"
                checked={formData.agree_terms}
                onChange={handleInputChange}
                className="mt-1 text-indigo-600 focus:ring-indigo-500"
                style={{ 
                  width: '1rem', 
                  height: '1rem', 
                  minWidth: '1rem',
                  minHeight: '1rem',
                  borderWidth: '1px',
                  borderColor: '#d1d5db',
                  borderRadius: '0.25rem',
                  boxSizing: 'border-box'
                }}
                required
              />
              <label htmlFor="agree_terms" className="ml-3 text-sm">
                I have read, understood, and agree to the {campaign?.name || 'competition'}&nbsp;
                <button
                  type="button"
                  onClick={() => setShowTermsModal(true)}
                  className="text-indigo-600 hover:text-indigo-800 underline cursor-pointer"
                >
                  terms & conditions
                </button>
                {' '}and Good Drinks{' '}
                <a
                  href="https://gooddrinks.com.au/privacy-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-800 underline"
                >
                  privacy policy
                </a>
                . *
              </label>
            </div>
            {formErrors.agree_terms && (
              <p className="mt-2 text-sm text-red-600">{formErrors.agree_terms}</p>
            )}
            <div className="flex items-start">
              <input
                type="checkbox"
                name="marketing_consent"
                id="marketing_consent"
                checked={formData.marketing_consent}
                onChange={handleInputChange}
                className="mt-1 text-indigo-600 focus:ring-indigo-500"
                style={{ 
                  width: '1rem', 
                  height: '1rem',
                  minWidth: '1rem',
                  minHeight: '1rem',
                  borderWidth: '1px',
                  borderColor: '#d1d5db',
                  borderRadius: '0.25rem',
                  boxSizing: 'border-box'
                }}
              />
              <label htmlFor="marketing_consent" className="ml-3 text-sm">
                Yes, I would like to receive updates including news, special offers and promotions
              </label>
            </div>
          </div>

          {/* 400 Bad Request Error Alert */}
          {formErrors.submit && formErrors.submit.includes('Bad Request:') && (
            <div className="mt-4 p-6 bg-red-50 border-2 border-red-400 rounded-lg">
              <div className="flex items-start">
                <svg className="w-8 h-8 text-red-600 mr-4 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-red-800 mb-2">Submission Error</h3>
                  <p className="text-lg text-red-700">{formErrors.submit}</p>
                  <div className="mt-3 text-sm text-red-600">
                    <p className="font-semibold mb-1">Please check:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>All required fields are completed</li>
                      <li>Email address is valid</li>
                      <li>Phone number is properly formatted</li>
                      <li>Receipt file is uploaded (if required)</li>
                      <li>Terms and conditions are accepted</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Other Error Messages */}
          {formErrors.submit && !validationSteps && !(isEntryLimitError(formErrors.submit) || isReceiptImageQualityError(formErrors.submit) || formErrors.submit.includes('Bad Request:')) && (
            <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700">
              {formErrors.submit}
            </div>
          )}
          
          {(formErrors.submit && isReceiptImageQualityError(formErrors.submit)) && (
            <div className="mt-4 p-6 bg-yellow-50 border-2 border-yellow-400 rounded-lg">
              <div className="flex items-start">
                <Info className="w-8 h-8 text-yellow-600 mr-4 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-yellow-800 mb-2">Receipt Image Unclear</h3>
                  <p className="text-lg text-yellow-700">
                    We could not read the receipt from your upload. Please scan your receipt or retake a clear photo, then try uploading it again.
                  </p>
                  <div className="mt-3 text-sm text-yellow-600">
                    <p className="font-semibold mb-1">Quick tips for a successful upload:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Capture the entire receipt in a single image</li>
                      <li>Use good lighting and avoid shadows or glare</li>
                      <li>Ensure the text is sharp and easy to read</li>
                      <li>Upload a higher-resolution scan or photo if available</li>
                    </ul>
                    <p className="mt-3 italic">{formErrors.submit}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {(formErrors.submit && isEntryLimitError(formErrors.submit)) && (
            <div className="mt-4 p-6 bg-yellow-50 border-2 border-yellow-400 rounded-lg">
              <div className="flex items-start">
                <svg className="w-8 h-8 text-yellow-600 mr-4 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-yellow-800 mb-2">Entry Limit Reached</h3>
                  <p className="text-lg text-yellow-700">{formErrors.submit}</p>
                  <p className="text-sm text-yellow-600 mt-2">Thank you for your interest in this campaign!</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Product Match Failure Error */}
          {formErrors.submit && (formErrors.submit.includes('No product match found') || formErrors.submit.includes('Receipt does not contain') || formErrors.submit.includes('No matching products found')) && (
            <div className="mt-4 p-6 bg-red-50 border-2 border-red-400 rounded-lg">
              <div className="flex items-start">
                <XCircle className="w-8 h-8 text-red-600 mr-4 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-red-800 mb-2">Product Not Found on Receipt</h3>
                  <p className="text-lg text-red-700">{formErrors.submit}</p>
                  <div className="mt-3 text-sm text-red-600">
                    <p className="font-semibold mb-1">Please check:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>You've purchased the correct product for this campaign</li>
                      <li>The product name matches what you selected in the form</li>
                      <li>The pack size matches (e.g., 24-pack vs 30-pack)</li>
                      <li>Your receipt is clear and readable</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {(validationSteps || (submitting && showValidationProgress)) && !(formErrors.submit && isEntryLimitError(formErrors.submit)) && (
            <div className="mt-4 p-6 bg-gray-50 border border-gray-200 rounded-lg">
              <h4 className="font-semibold mb-4 text-gray-800">Receipt Validation Process</h4>
              
              <div className="relative">
                {(() => {
                  const stepsToRender = normalizeValidationSteps(validationSteps).filter(({ step }) => step && typeof step === 'object');
                  return stepsToRender.map(({ stepKey, step }, index) => {
                    const derivedKey = stepKey || `step${index + 1}`;
                    const normalizedKey = String(derivedKey).toLowerCase();
                    const numberMatch = normalizedKey.match(/(\d+)/);
                    const stepNumber = numberMatch ? parseInt(numberMatch[1], 10) : index + 1;
                    const status = step.status || 'pending';
                    const isComplete = status === 'completed' || status === 'success';
                    const isError = status === 'failed' || status === 'error';
                    const isInProgress = status === 'in_progress' || status === 'processing' || status === 'running';
                    const indicatorClass = isComplete
                      ? 'bg-green-500'
                      : isError
                      ? 'bg-red-500'
                      : isInProgress
                      ? 'bg-blue-500 animate-pulse'
                      : 'bg-gray-400';
                    const titleClass = isComplete
                      ? 'text-green-600'
                      : isError
                      ? 'text-red-600'
                      : isInProgress
                      ? 'text-blue-600'
                      : 'text-gray-500';
                    const messageClass = isComplete
                      ? 'text-gray-700'
                      : isError
                      ? 'text-red-600'
                      : isInProgress
                      ? 'text-gray-600'
                      : 'text-gray-500';
                    const message =
                      step.message ||
                      step.details ||
                      step.statusMessage ||
                      step.description ||
                      'Waiting...';

                    return (
                      <div key={derivedKey} className="relative flex items-start mb-8 last:mb-0">
                        {/* Vertical line connector */}
                        {index < stepsToRender.length - 1 && (
                          <div className="absolute left-5 top-10 w-0.5 h-full bg-gray-300"></div>
                        )}

                        {/* Step indicator */}
                        <div className="relative flex items-center justify-center mr-4">
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-white transition-all ${indicatorClass}`}
                          >
                            {isComplete ? (
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            ) : isError ? (
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            ) : isInProgress ? (
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <span>{stepNumber}</span>
                            )}
                          </div>
                        </div>

                        {/* Step content */}
                        <div className="flex-1 pt-1">
                          <p className={`font-semibold text-sm uppercase tracking-wide ${titleClass}`}>
                            {step.name || step.title || `Step ${stepNumber}`}
                          </p>
                          <p className={`text-sm mt-1 ${messageClass}`}>{message}</p>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || (formErrors.submit && isEntryLimitError(formErrors.submit)) || !isEntryRequirementsMet()}
            className="w-full bg-indigo-600 text-white py-3 px-4 rounded-md hover:bg-indigo-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            style={{ backgroundColor: (formErrors.submit && isEntryLimitError(formErrors.submit)) || !isEntryRequirementsMet() ? '#9CA3AF' : (campaign?.accent_color || '#4F46E5') }}
          >
            {submitting ? 'Submitting...' : (formErrors.submit && isEntryLimitError(formErrors.submit)) ? 'Entry Limit Reached' : !isEntryRequirementsMet() ? getDisabledButtonText() : 'Submit Entry'}
          </button>
        </form>
      </div>
    );
  };

  const renderMechanicsDescription = () => {
    const mechanics = {
      'entry_second_chance': 'Enter for your chance to win! Every valid entry also gets a second chance draw.',
      'simple_entry': 'Enter by filling out the form and uploading your receipt.',
      'major_prize': 'Enter for your chance to win our grand prize!',
      'major_minor_prize': 'Win big or win small - everyone has a chance!',
      'rule_based_draw': 'Enter to win based on our special rules and conditions.',
      'one_in_x_wins': 'Great odds! Limited instant win prizes available.',
      'win_one_of_x': 'Multiple prizes up for grabs!',
      'cashback': 'Get cash back on your purchase!',
      'bounce_back': 'Enter now and receive an instant bonus!'
    };
    
    return mechanics[campaign?.mechanics_type] || 'Enter for your chance to win!';
  };

  const isEntryRequirementsMet = () => {
    // Check if receipt is required but not uploaded
    if (campaign?.requires_receipt && !receiptFile) {
      return false;
    }
    
    // Check if any Entry Method 2 fields with quantity inputs have valid requirements
    for (const field of allFields) {
      if (field.is_multiselect && field.enable_quantity_input && field.field_options?.options) {
        const fieldKey = field.field_name;
        const selectedDrinks = quantityInputs[fieldKey] || {};
        const drinkOptions = field.field_options.options || [];
        
        // Calculate totals dynamically based on drink types
        const individualDrinksTotal = drinkOptions
          .filter(drink => !drink.toLowerCase().includes('jug'))
          .reduce((sum, drink) => sum + (selectedDrinks[drink] || 0), 0);
        
        const jugsTotal = drinkOptions
          .filter(drink => drink.toLowerCase().includes('jug'))
          .reduce((sum, drink) => sum + (selectedDrinks[drink] || 0), 0);
        
        // If this field is visible and has invalid requirements, return false
        if (isFieldVisible(field) && !(individualDrinksTotal >= 2 || jugsTotal >= 1)) {
          return false;
        }
      }
    }
    return true;
  };

  const getDisabledButtonText = () => {
    // Check if receipt is required but not uploaded
    if (campaign?.requires_receipt && !receiptFile) {
      return `${getReceiptUploadLabel(form)} Required`;
    }
    
    // Check if any Entry Method 2 fields with quantity inputs have invalid requirements
    for (const field of allFields) {
      if (field.is_multiselect && field.enable_quantity_input && field.field_options?.options) {
        const fieldKey = field.field_name;
        const selectedDrinks = quantityInputs[fieldKey] || {};
        const drinkOptions = field.field_options.options || [];
        
        // Calculate totals dynamically based on drink types
        const individualDrinksTotal = drinkOptions
          .filter(drink => !drink.toLowerCase().includes('jug'))
          .reduce((sum, drink) => sum + (selectedDrinks[drink] || 0), 0);
        
        const jugsTotal = drinkOptions
          .filter(drink => drink.toLowerCase().includes('jug'))
          .reduce((sum, drink) => sum + (selectedDrinks[drink] || 0), 0);
        
        // If this field is visible and has invalid requirements, return specific message
        if (isFieldVisible(field) && !(individualDrinksTotal >= 2 || jugsTotal >= 1)) {
          return 'Entry Requirements Not Met';
        }
      }
    }
    
    return 'Entry Requirements Not Met';
  };

  const normalizeValidationSteps = (steps) => {
    if (!steps) return [];

    if (Array.isArray(steps)) {
      return steps
        .filter(step => step && typeof step === 'object')
        .map((step, idx) => ({
          stepKey: step.stepKey || step.key || `step${idx + 1}`,
          step
        }));
    }

    return Object.entries(steps || {})
      .filter(([stepKey, step]) => {
        if (!step || typeof step !== 'object') return false;
        if (!stepKey) return false;
        return /^step\d+$/i.test(stepKey);
      })
      .sort(([keyA], [keyB]) => {
        const numFromKey = (key) => {
          const match = String(key).match(/(\d+)/);
          return match ? parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER;
        };
        return numFromKey(keyA) - numFromKey(keyB);
      })
      .map(([stepKey, step]) => ({
        stepKey,
        step
      }));
  };

  const isFieldVisible = (field) => {
    if (!field.field_conditions?.visibility) {
      return true; // No conditions, always visible
    }
    
    const { type, field: conditionField, value, operator } = field.field_conditions.visibility;
    const fieldValue = formData[conditionField];
    
    let conditionMet = false;
    
    switch (operator || 'equals') {
      case 'equals':
        conditionMet = fieldValue === value;
        break;
      case 'not_equals':
        conditionMet = fieldValue !== value;
        break;
      case 'contains':
        conditionMet = fieldValue && fieldValue.includes(value);
        break;
      case 'not_contains':
        conditionMet = !fieldValue || !fieldValue.includes(value);
        break;
      default:
        conditionMet = fieldValue === value;
    }
    
    return type === 'show_if' ? conditionMet : !conditionMet;
  };

  const renderField = (field) => {
    const value = formData[field.field_name] || '';
    const error = formErrors[field.field_name];
    const recording = videoRecordings[field.field_name];
    const recordingStatus = videoRecordingStatus[field.field_name] || {};
    
    switch (field.field_type) {
      case 'phone':
        return (
          <div key={field.id}>
            <label className="block text-base font-medium mb-2">
              {field.field_label} {field.is_required ? <span className="text-red-500">*</span> : <span className="text-gray-500 text-sm">(Optional)</span>}
            </label>
            <PhoneInput
              country={'au'}
              value={value}
              onChange={(phone) => {
                const event = {
                  target: {
                    name: field.field_name,
                    value: phone
                  }
                };
                handleInputChange(event);
              }}
              inputProps={{
                name: field.field_name,
                required: field.is_required
              }}
              specialLabel={false}
              containerClass="phone-input-container"
              enableSearch={true}
              searchPlaceholder="Search countries..."
              preferredCountries={['ph', 'us', 'gb', 'au', 'ca']}
              placeholder={field.field_placeholder || 'Enter phone number'}
            />
            {error ? <p className="mt-1 text-sm text-red-600">{error}</p> : null}
          </div>
        );

      case 'video_recorder':
        return (
          <div key={field.id}>
            <label className="block text-base font-medium mb-2">
              {field.field_label} {field.is_required ? <span className="text-red-500">*</span> : <span className="text-gray-500 text-sm">(Optional)</span>}
            </label>
            <div className="border border-gray-300 rounded-2xl p-4 bg-white space-y-4">
              <div className="aspect-video rounded-xl overflow-hidden bg-black flex items-center justify-center">
                {recordingStatus.isInitializing ? (
                  <span className="text-sm text-white">Starting camera...</span>
                ) : recordingStatus.isRecording || recording?.previewUrl ? (
                  <video
                    ref={(element) => {
                      videoPreviewRefs.current[field.field_name] = element;
                      if (!element) return;

                      const stream = mediaStreamRefs.current[field.field_name];
                      if (stream) {
                        element.srcObject = stream;
                        element.muted = true;
                        element.play().catch(() => {});
                      } else {
                        element.srcObject = null;
                      }
                    }}
                    className="w-full h-full object-cover"
                    autoPlay={recordingStatus.isRecording}
                    muted={recordingStatus.isRecording}
                    playsInline
                    controls={!recordingStatus.isRecording && !!recording?.previewUrl}
                    src={!recordingStatus.isRecording ? recording?.previewUrl : undefined}
                  />
                ) : (
                  <span className="text-sm text-white/80">Camera preview will appear here</span>
                )}
              </div>

              <div className="flex flex-wrap gap-3">
                {!recordingStatus.isRecording ? (
                  <button
                    type="button"
                    onClick={() => startVideoRecording(field)}
                    disabled={recordingStatus.isInitializing}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    {recording ? 'Record Again' : 'Start Recording'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => stopVideoRecording(field.field_name)}
                    className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
                  >
                    Stop Recording
                  </button>
                )}

                {recording && !recordingStatus.isRecording && (
                  <button
                    type="button"
                    onClick={() => clearVideoRecording(field.field_name)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Remove Video
                  </button>
                )}
              </div>

              <div className="text-sm text-gray-500 space-y-1">
                <p>Record your video here. It will upload only after the full form is submitted.</p>
                <p>Maximum file size: {VIDEO_RECORDING_MAX_SIZE_MB}MB.</p>
                {recording?.size ? (
                  <p className="text-green-600 font-medium">
                    Recorded video ready ({(recording.size / (1024 * 1024)).toFixed(1)} MB)
                  </p>
                ) : null}
              </div>

              {error ? <p className="text-sm text-red-600">{error}</p> : null}
            </div>
          </div>
        );
        
      case 'text':
      case 'email':
      case 'date':
        return (
          <div key={field.id}>
            <label className="block text-base font-medium mb-2">
              {field.field_label} {field.is_required ? <span className="text-red-500">*</span> : <span className="text-gray-500 text-sm">(Optional)</span>}
            </label>
            <input
              type={field.field_type === 'email' ? 'email' : field.field_type === 'date' ? 'date' : 'text'}
              name={field.field_name}
              value={value}
              onChange={(e) => handleInputChange(e)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder={field.field_placeholder || ''}
              required={field.is_required}
            />
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>
        );
        
      case 'textarea':
        return (
          <div key={field.id}>
            <label className="block text-base font-medium mb-2">
              {field.field_label} {field.is_required ? <span className="text-red-500">*</span> : <span className="text-gray-500 text-sm">(Optional)</span>}
            </label>
            <textarea
              name={field.field_name}
              value={value}
              onChange={(e) => handleInputChange(e)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder={field.field_placeholder || ''}
              rows={4}
              required={field.is_required}
            />
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>
        );
        
      case 'dropdown':
        if (field.is_multiselect) {
          // Check if this field has quantity input enabled
          if (field.enable_quantity_input) {
            // Custom quantity UI for Entry Method 2 drinks
            const drinkOptions = field.field_options?.options || [];
            const fieldKey = field.field_name;
            const selectedDrinks = quantityInputs[fieldKey] || {};
            
            // Calculate totals dynamically based on drink types
            // Individual drinks = all options that DON'T contain "Jug"
            // Jugs = all options that contain "Jug"
            const individualDrinksTotal = drinkOptions
              .filter(drink => !drink.toLowerCase().includes('jug'))
              .reduce((sum, drink) => sum + (selectedDrinks[drink] || 0), 0);
            
            const jugsTotal = drinkOptions
              .filter(drink => drink.toLowerCase().includes('jug'))
              .reduce((sum, drink) => sum + (selectedDrinks[drink] || 0), 0);
            
            const isValid = individualDrinksTotal >= 2 || jugsTotal >= 1;
            
            return (
              <div key={field.id}>
                <label className="block text-base font-medium mb-2">
                  {field.field_label} {field.is_required ? <span className="text-red-500">*</span> : <span className="text-gray-500 text-sm">(Optional)</span>}
                </label>
                <p className="text-sm text-gray-600 mb-3">
                  Select drinks and quantities. Need 2+ individual drinks OR 1+ jug.
                </p>
                
                {/* Dynamically render all drink options from field configuration */}
                <div className="space-y-3">
                  {drinkOptions.map(drink => (
                    <div key={drink} className="border rounded-lg p-4 bg-white">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id={`drink-${drink}`}
                            checked={(selectedDrinks[drink] || 0) > 0}
                            onChange={(e) => {
                              setQuantityInputs(prev => ({
                                ...prev,
                                [fieldKey]: {
                                  ...prev[fieldKey],
                                  [drink]: e.target.checked ? 1 : 0
                                }
                              }));
                            }}
                            className="h-5 w-5 text-indigo-600 rounded mr-3"
                          />
                          <label htmlFor={`drink-${drink}`} className="text-sm font-medium cursor-pointer">
                            {drink}
                          </label>
                        </div>
                        
                        {/* Show quantity controls only if this drink is selected */}
                        {(selectedDrinks[drink] || 0) > 0 && (
                          <div className="flex items-center space-x-2">
                            <button
                              type="button"
                              onClick={() => setQuantityInputs(prev => ({
                                ...prev,
                                [fieldKey]: {
                                  ...prev[fieldKey],
                                  [drink]: Math.max(0, (prev[fieldKey]?.[drink] || 0) - 1)
                                }
                              }))}
                              className="w-8 h-8 bg-gray-200 rounded hover:bg-gray-300"
                            >
                              -
                            </button>
                            <input
                              type="number"
                              value={selectedDrinks[drink] || 0}
                              onChange={(e) => setQuantityInputs(prev => ({
                                ...prev,
                                [fieldKey]: {
                                  ...prev[fieldKey],
                                  [drink]: Math.max(0, parseInt(e.target.value) || 0)
                                }
                              }))}
                              min="0"
                              className="w-16 text-center leading-relaxed border rounded px-2 py-1"
                            />
                            <button
                              type="button"
                              onClick={() => setQuantityInputs(prev => ({
                                ...prev,
                                [fieldKey]: {
                                  ...prev[fieldKey],
                                  [drink]: (prev[fieldKey]?.[drink] || 0) + 1
                                }
                              }))}
                              className="w-8 h-8 bg-gray-200 rounded hover:bg-gray-300"
                            >
                              +
                            </button>
                            <span className="text-xs text-gray-500 ml-2">Qty</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Entry Requirements Status */}
                <div className="mt-4">
                  <p className={`text-sm font-medium ${isValid ? 'text-green-600' : 'text-red-600'}`}>
                    {isValid ? '✓ Entry requirements met!' : '✗ Need 2+ individual drinks OR 1+ jug'}
                  </p>
                </div>
                
                {/* Totals Section */}
                <div className="mt-2 p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Individual drinks: </span>
                      <span className={individualDrinksTotal >= 2 ? 'text-green-600' : 'text-gray-600'}>
                        {individualDrinksTotal} {individualDrinksTotal >= 2 ? '✓' : ''}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Jugs: </span>
                      <span className={jugsTotal >= 1 ? 'text-green-600' : 'text-gray-600'}>
                        {jugsTotal} {jugsTotal >= 1 ? '✓' : ''}
                      </span>
                    </div>
                  </div>
                </div>
                
                {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
              </div>
            );
          } else {
            // Regular multiselect dropdown
            const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);
            
            return (
              <div key={field.id}>
                <label className="block text-base font-medium mb-2">
                  {field.field_label} {field.is_required ? <span className="text-red-500">*</span> : <span className="text-gray-500 text-sm">(Optional)</span>}
                </label>
                <select
                  name={field.field_name}
                  value={selectedValues}
                  onChange={(e) => {
                    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                    handleInputChange({
                      target: {
                        name: field.field_name,
                        value: selectedOptions
                      }
                    });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required={field.is_required}
                  multiple
                  size={Math.min(field.field_options?.options?.length || 3, 6)}
                >
                  {field.field_options?.options?.map((option, idx) => (
                    <option key={idx} value={option}>{option}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple options</p>
                {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
              </div>
            );
          }
        } else {
          // Single select dropdown
          return (
            <div key={field.id}>
              <label className="block text-base font-medium mb-2">
                {field.field_label} {field.is_required ? <span className="text-red-500">*</span> : <span className="text-gray-500 text-sm">(Optional)</span>}
              </label>
              <select
                name={field.field_name}
                value={value}
                onChange={(e) => handleInputChange(e)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required={field.is_required}
              >
                <option value="">Choose an option</option>
                {field.field_options?.options?.map((option, idx) => (
                  <option key={idx} value={option}>{option}</option>
                ))}
              </select>
              {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
            </div>
          );
        }
        
      case 'radio':
        return (
          <div key={field.id}>
            <label className="block text-base font-medium mb-2">
              {field.field_label} {field.is_required ? <span className="text-red-500">*</span> : <span className="text-gray-500 text-sm">(Optional)</span>}
            </label>
            <div className="space-y-2">
              {field.field_options?.options?.map((option, idx) => (
                <label key={idx} className="flex items-center">
                  <input
                    type="radio"
                    name={`radio_${field.field_name}`}
                    value={option}
                    checked={value === option}
                    onChange={() => {
                      setFormData(prev => ({ ...prev, [field.field_name]: option }));
                      // Clear max entries error if this is email or phone field
                      if ((field.field_name === 'email' || field.field_name === 'phone') && formErrors.submit) {
                        if (isEntryLimitError(formErrors.submit)) {
                          setFormErrors(prev => ({ ...prev, submit: '' }));
                        }
                      }
                    }}
                    className="mr-2"
                    required={field.is_required && idx === 0}
                  />
                  <span className="text-sm">{option}</span>
                </label>
              ))}
            </div>
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>
        );
        
      case 'checkbox':
        return (
          <div key={field.id}>
            <label className="block text-base font-medium mb-2">
              {field.field_label} {field.is_required ? <span className="text-red-500">*</span> : <span className="text-gray-500 text-sm">(Optional)</span>}
            </label>
            <div className="space-y-2">
              {field.field_options?.options?.map((option, idx) => (
                <label key={idx} className="flex items-center">
                  <input
                    type="checkbox"
                    value={option}
                    checked={Array.isArray(value) ? value.includes(option) : false}
                    onChange={(e) => {
                      const currentValues = Array.isArray(value) ? value : [];
                      if (e.target.checked) {
                        setFormData(prev => ({ ...prev, [field.field_name]: [...currentValues, option] }));
                      } else {
                        setFormData(prev => ({ ...prev, [field.field_name]: currentValues.filter(v => v !== option) }));
                      }
                      // Clear max entries error if this is email or phone field
                      if ((field.field_name === 'email' || field.field_name === 'phone') && formErrors.submit) {
                        if (isEntryLimitError(formErrors.submit)) {
                          setFormErrors(prev => ({ ...prev, submit: '' }));
                        }
                      }
                    }}
                    className="mr-2"
                  />
                  <span className="text-sm">{option}</span>
                </label>
              ))}
            </div>
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>
        );
        
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading campaign...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen campaign-public-page"
      style={{ fontFamily: getCampaignParagraphFontFamily(campaign), color: campaign?.primary_color || '#1F2937' }}
    >
      {/* Age Verification Popup */}
      {showAgeVerification && !ageVerified && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
            {/* Logo */}
            {campaign?.logo_url && (
              <div className="flex justify-center mb-6">
                <img 
                  src={`${ASSET_BASE_URL}${campaign.logo_url}`} 
                  alt={campaign.name}
                  className="h-16"
                />
              </div>
            )}
            
            {!showUnderageMessage ? (
              <>
                <h2 className="text-3xl font-bold text-center leading-relaxed mb-4 text-gray-900">Age Verification Required</h2>
                <p className="text-center leading-relaxed text-gray-700 mb-8">
                  You must be 18 years or older to enter this promotion. Please confirm your age to continue.
                </p>
                
                <div className="space-y-4">
                  <button
                    onClick={() => handleAgeVerification(true)}
                    className="w-full py-4 px-6 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl transition-colors shadow-lg"
                  >
                    Yes, I am 18 or older
                  </button>
                  <button
                    onClick={() => handleAgeVerification(false)}
                    className="w-full py-4 px-6 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-xl transition-colors"
                  >
                    No, I am under 18
                  </button>
                </div>
                
                <p className="text-center leading-relaxed text-xs text-gray-500 mt-6">
                  By entering this site you agree to our Terms & Conditions and Privacy Policy.
                </p>
              </>
            ) : (
              <div className="text-center leading-relaxed">
                <h2 className="text-3xl font-bold mb-4 text-gray-900">Sorry!</h2>
                <p className="text-lg text-gray-700 mb-8">
                  We're sorry, you must be of legal drinking age to view our site.
                </p>
                <button
                  onClick={() => window.location.href = 'https://www.google.com'}
                  className="py-3 px-8 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-xl transition-colors"
                >
                  Exit Site
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showThankYou ? (
        // Thank You Page
        <div className="min-h-screen flex flex-col">
          {/* Logo Section */}
          <div className="py-4">
            <div className="container mx-auto px-4">
              {campaign?.logo_url && (
                <img 
                  src={`${ASSET_BASE_URL}${campaign.logo_url}`} 
                  alt={campaign.name}
                  className="h-16 md:h-20"
                />
              )}
            </div>
          </div>
          
          {/* Thank You Content with Background */}
          <div className="flex-1 bg-gray-50 flex items-center justify-center py-8 overflow-x-hidden">
            <div className="container mx-auto px-4">
              <div 
                className="relative w-full aspect-[2933/1382] rounded-lg shadow-lg overflow-hidden mb-8 bg-cover bg-center bg-no-repeat"
                style={{
                  backgroundColor: campaign?.primary_color || '#1F2937',
                  backgroundImage: campaign?.thank_you_background_url ? `url(${ASSET_BASE_URL}${campaign.thank_you_background_url})` : campaign?.background_image_url ? `url(${ASSET_BASE_URL}${campaign.background_image_url})` : 'none'
                }}
              >
                <div className="absolute inset-0 bg-black bg-opacity-30"></div>
              </div>
              
              <div className="w-full text-center leading-relaxed">
                
                {/* Cashback voucher — guaranteed reward, shown first for cashback campaigns */}
                {cashbackVoucher ? (
                  <div className="mb-6">
                    <div className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white p-6 sm:p-8 lg:p-10 rounded-xl shadow-[0_20px_45px_rgba(16,185,129,0.35)] w-full">
                      <div className="max-w-lg mx-auto text-center space-y-6">
                        <div>
                          <span className="inline-flex items-center rounded-full border border-white/40 bg-white/20 px-3 py-1 text-xs uppercase tracking-[0.3em]">
                            Cashback Reward
                          </span>
                        </div>

                        <div className="space-y-2">
                          <p className="text-xs uppercase tracking-[0.35em] text-white/70">Thank you for your purchase</p>
                          <h3 className="text-2xl sm:text-3xl font-bold leading-tight">
                            Here&rsquo;s your cashback voucher!
                          </h3>
                        </div>

                        <div className="bg-white/95 rounded-2xl p-6 text-gray-900 shadow-inner">
                          <p className="text-xs sm:text-sm text-gray-500 mb-2">Your Voucher Code</p>
                          <p className="text-2xl sm:text-3xl lg:text-4xl font-mono tracking-widest text-gray-900 break-all">
                            {cashbackVoucher.code}
                          </p>
                        </div>

                        <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 p-4 text-sm text-white/90">
                          <p>Save this code or screenshot this page. A confirmation email with redemption instructions is on its way to your inbox.</p>
                        </div>

                      </div>
                    </div>
                  </div>
                ) : giftCard ? (
                  <div
                    className="mb-6 w-full text-center leading-relaxed"
                    style={{
                      fontFamily: getCampaignParagraphFontFamily(campaign),
                    }}
                  >
                    {winnerParsed ? (
                      <>
                        <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4 drop-shadow-[0_10px_18px_rgba(34,197,94,0.35)]" />
                        {winnerParsed.titleHtml != null ? (
                          <h2
                            className="text-2xl md:text-3xl font-bold mb-3 w-full max-w-none [&_a]:underline"
                            style={{ color: campaign?.primary_color || '#1F2937' }}
                            dangerouslySetInnerHTML={{ __html: winnerParsed.titleHtml }}
                          />
                        ) : (
                          <h2
                            className="text-2xl md:text-3xl font-bold mb-3 w-full max-w-none [&_a]:underline"
                            style={{ color: campaign?.primary_color || '#1F2937' }}
                          >
                            {winnerParsed.titlePlain}
                          </h2>
                        )}
                        {winnerParsed.bodyHtml != null && (
                          <div
                            className="max-w-4xl mx-auto w-full text-lg md:text-xl font-normal [&_p]:font-normal [&_a]:underline"
                            style={{ color: campaign?.primary_color || '#1F2937' }}
                            dangerouslySetInnerHTML={{ __html: winnerParsed.bodyHtml }}
                          />
                        )}
                      </>
                    ) : null}
                  </div>
                ) : instantWinResult && instantWinResult.eligible && instantWinResult.won && entryStatus === 'approved' ? (
                  <div className="mb-6">
                    <div className="bg-gradient-to-r from-yellow-400 to-orange-400 text-black p-6 sm:p-8 lg:p-10 rounded-xl shadow-[0_20px_45px_rgba(252,211,77,0.45)] w-full transition-shadow duration-300">
                      <div className="grid md:grid-cols-2 gap-6 lg:gap-10 items-center">
                        <div className="relative text-left bg-white/10 backdrop-blur-md rounded-3xl border border-white/30 px-6 sm:px-8 lg:px-10 py-6 sm:py-8 overflow-hidden shadow-[0_20px_40px_rgba(15,23,42,0.12)]">
                          <div className="pointer-events-none absolute inset-0">
                            <div className="absolute -top-14 -right-10 w-40 h-40 bg-white/25 blur-3xl rounded-full"></div>
                            <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-tr from-white/10 via-transparent to-white/20 opacity-80"></div>
                          </div>
                          <div className="relative space-y-6">
                            <div className="flex items-center gap-4">
                              {instantWinResult.logoUrl && (
                                <div className="h-16 sm:h-20 w-auto flex items-center justify-center">
                                  <img
                                    src={`${ASSET_BASE_URL}${instantWinResult.logoUrl}`}
                                    alt={`${instantWinResult.prize} logo`}
                                    className="max-h-full max-w-[200px] object-contain drop-shadow-[0_12px_30px_rgba(0,0,0,0.25)]"
                                    loading="lazy"
                                  />
                                </div>
                              )}
                              <span className="inline-flex items-center rounded-full border border-white/40 bg-white/20 px-3 py-1 text-xs uppercase tracking-[0.3em] text-white/90">
                                Instant Winner
                              </span>
                            </div>
                            <div className="space-y-3 text-white">
                              <p className="text-xs uppercase tracking-[0.35em] text-white/70">Congratulations</p>
                              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight drop-shadow-sm">
                                You've won a {instantWinResult.prize}!
                              </h3>
                              {instantWinPrizeValue !== null && (
                                <div className="inline-flex items-center gap-2 rounded-full bg-white/15 border border-white/30 px-4 py-2 text-sm font-medium text-white/90 shadow-inner">
                                  <span className="text-white/70 uppercase tracking-[0.2em] text-xs">Prize Value</span>
                                  <span className="text-base sm:text-lg font-semibold">${instantWinPrizeValue.toFixed(2)}</span>
                                </div>
                              )}
                            </div>
                            <div className="pt-4 border-t border-white/25 text-white/80 text-sm">
                              <div className="rounded-2xl border border-white/30 bg-white/10 px-5 py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]">
                                <span className="text-xs uppercase tracking-[0.25em] text-white/60">Campaign</span>
                                <p className="mt-2 text-base sm:text-lg font-semibold text-white/95 leading-snug">
                                  {campaign?.name || 'Current Promotion'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="bg-white/95 rounded-3xl p-6 sm:p-8 lg:p-10 text-gray-900 shadow-[0_18px_40px_rgba(15,23,42,0.18)] border border-white/40">
                          <div className="flex flex-col gap-6">
                            <div className="w-full">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-xs uppercase tracking-[0.2em] text-gray-400">Claim Confirmation</p>
                                  <h4 className="text-lg sm:text-xl font-semibold text-gray-900 mt-1">Redeem your prize</h4>
                                </div>
                                <div className="flex -space-x-1">
                                  <span className="inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500"></span>
                                  <span className="inline-flex h-2.5 w-2.5 rounded-full bg-amber-400"></span>
                                  <span className="inline-flex h-2.5 w-2.5 rounded-full bg-sky-400"></span>
                                </div>
                              </div>
                              <div className="mt-4 bg-gray-50 border border-gray-200 rounded-2xl px-5 py-4 sm:px-6 sm:py-5 shadow-inner">
                                <p className="text-xs sm:text-sm text-gray-500 mb-2">Your Claim Code</p>
                                <p className="text-xl sm:text-2xl lg:text-3xl font-mono tracking-widest text-gray-900">
                                  {instantWinResult.claimCode}
                                </p>
                              </div>
                            </div>

                            <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5 shadow-sm">
                              <p className="text-sm text-gray-700">
                                Save this code or screenshot this page. A confirmation email with redemption instructions is on its way to your inbox.
                              </p>
                              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs sm:text-sm text-gray-500">
                                <div className="flex items-start gap-2">
                                  <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 mt-1"></span>
                                  <p>Present this code at participating locations to claim your reward.</p>
                                </div>
                                <div className="flex items-start gap-2">
                                  <span className="inline-block h-2 w-2 rounded-full bg-amber-500 mt-1"></span>
                                  <p>Need help? Reply to the confirmation email and our support team will assist you.</p>
                                </div>
                              </div>
                            </div>

                            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-5 text-left">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div>
                                  <p className="text-sm font-semibold text-emerald-600">Thank you for participating!</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Regular success message for non-winners */}
                    <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4 drop-shadow-[0_10px_18px_rgba(34,197,94,0.35)]" />
                    {thankYouParsed.titleHtml != null ? (
                      <h2
                        className="text-3xl font-bold mb-3 w-full max-w-none"
                        style={{ color: campaign?.primary_color || '#1F2937' }}
                        dangerouslySetInnerHTML={{ __html: thankYouParsed.titleHtml }}
                      />
                    ) : (
                      <h2
                        className="text-3xl font-bold mb-3 w-full max-w-none"
                        style={{ color: campaign?.primary_color || '#1F2937' }}
                      >
                        {thankYouParsed.titlePlain}
                      </h2>
                    )}
                    {thankYouParsed.bodyHtml != null && (
                      <div
                        className="max-w-4xl mx-auto w-full text-base md:text-lg mb-4 leading-relaxed [&_p]:mb-2 [&_p:last-child]:mb-0"
                        style={{ color: campaign?.primary_color || '#1F2937' }}
                        dangerouslySetInnerHTML={{ __html: thankYouParsed.bodyHtml }}
                      />
                    )}
                  </>
                )}
                <button
                  onClick={() => {
                    setShowThankYou(false);
                    setInstantWinResult(null);
                    setCashbackVoucher(null);
                    setWaitingForInstantWin(false);
                    setEntrySequence(null);
                    setEntryNumber('');
                  }}
                  className="bg-indigo-600 text-white px-8 py-3 rounded-md hover:bg-indigo-700 transition-colors font-medium"
                  style={{ backgroundColor: campaign?.accent_color || '#4F46E5' }}
                >
                  Submit Another Entry
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Main Campaign Page
        <>
          {/* Check if campaign is expired */}
          {campaign?.display_status === 'expired' ? (
            <div className="min-h-screen bg-gray-50">
              {/* Header with campaign theme */}
              <header className="bg-gray-50">
                {/* Logo Section */}
                <div className="pt-6 pb-4">
                  <div className="container mx-auto px-4">
                    {campaign?.logo_url && (
                      <img 
                        src={`${ASSET_BASE_URL}${campaign.logo_url}`} 
                        alt={campaign.name}
                        className="h-16 md:h-20"
                      />
                    )}
                  </div>
                </div>
                
                {/* Background Image Section */}
                <div className="pb-6">
                  <div className="container mx-auto px-4">
                    {campaign?.background_image_url ? (
                      <div 
                        className="relative h-[47.1vw] md:h-[47.1vw] lg:aspect-[2933/1382] bg-cover bg-center bg-no-repeat rounded-lg shadow-lg overflow-hidden"
                        style={{
                          backgroundColor: campaign?.primary_color || '#1F2937',
                          backgroundImage: `url(${ASSET_BASE_URL}${campaign.background_image_url})`
                        }}
                      >
                        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                          <div className="text-center leading-relaxed text-white">
                            <h1 className="text-4xl md:text-6xl font-bold mb-4">Campaign Has Ended</h1>
                            <p className="text-xl md:text-2xl">
                              This promotion ended on {new Date(campaign.end_date).toLocaleDateString('en-US', { 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric',
                                timeZone: 'Australia/Sydney'
                              })}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div 
                        className="h-32 rounded-lg shadow-lg flex items-center justify-center"
                        style={{ backgroundColor: campaign?.primary_color || '#1F2937' }}
                      >
                        <h1 className="text-3xl md:text-4xl font-bold text-white">Campaign Has Ended</h1>
                      </div>
                    )}
                  </div>
                </div>
              </header>

              {/* Message Content */}
              <main className="py-12">
                <div className="container mx-auto px-4 max-w-2xl">
                  <div className="bg-white rounded-lg shadow-lg p-8 text-center leading-relaxed">
                    <div className="mb-6">
                      <Clock className="mx-auto h-16 w-16 text-gray-400" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Thank You for Your Interest</h2>
                    <p className="text-gray-600 mb-6">
                      Sorry, this promotion has already exceeded its timeline. The campaign ended on {new Date(campaign.end_date).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric',
                        timeZone: 'Australia/Sydney'
                      })}.
                    </p>
                    <p className="text-sm text-gray-500">
                      Please check back for future promotions!
                    </p>
                  </div>
                </div>
              </main>
            </div>
          ) : campaign?.display_status === 'upcoming' ? (
            sections.length > 0 && isComingSoonPage ? (
              <div className="min-h-screen bg-gray-50">
                <header className="bg-gray-50">
                  <div className="pt-6 pb-4">
                    <div className="container mx-auto px-4">
                      {campaign?.logo_url && (
                        <img 
                          src={`${ASSET_BASE_URL}${campaign.logo_url}`} 
                          alt={campaign.name}
                          className="h-16 md:h-20"
                        />
                      )}
                    </div>
                  </div>
                </header>
                <main className="bg-gray-50">
                  {sections.map((section, index) => renderSectionInstance(section, index))}
                </main>
              </div>
            ) : (
              <div className="min-h-screen bg-gray-50">
                {/* Header with campaign theme */}
                <header className="bg-gray-50">
                  {/* Logo Section */}
                  <div className="pt-6 pb-4">
                    <div className="container mx-auto px-4">
                      {campaign?.logo_url && (
                        <img 
                          src={`${ASSET_BASE_URL}${campaign.logo_url}`} 
                          alt={campaign.name}
                          className="h-16 md:h-20"
                        />
                      )}
                    </div>
                  </div>
                  
                  {/* Background Image Section */}
                  <div className="pb-6">
                    <div className="container mx-auto px-4">
                      {campaign?.background_image_url ? (
                        <div 
                          className="relative h-[47.1vw] md:h-[47.1vw] lg:aspect-[2933/1382] bg-cover bg-center bg-no-repeat rounded-lg shadow-lg overflow-hidden"
                          style={{
                            backgroundColor: campaign?.primary_color || '#1F2937',
                            backgroundImage: `url(${ASSET_BASE_URL}${campaign.background_image_url})`
                          }}
                        >
                          <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                            <div className="text-center leading-relaxed text-white">
                              <h1 className="text-4xl md:text-6xl font-bold mb-4">Coming Soon!</h1>
                              <p className="text-xl md:text-2xl">
                                Campaign starts {new Date(campaign.start_date).toLocaleDateString('en-US', { 
                                  year: 'numeric', 
                                  month: 'long', 
                                  day: 'numeric',
                                  timeZone: 'Australia/Sydney'
                                })}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div 
                          className="h-32 rounded-lg shadow-lg flex items-center justify-center"
                          style={{ backgroundColor: campaign?.primary_color || '#1F2937' }}
                        >
                          <h1 className="text-3xl md:text-4xl font-bold text-white">Coming Soon!</h1>
                        </div>
                      )}
                    </div>
                  </div>
                </header>

                {/* Message Content */}
                <main className="py-12">
                  <div className="container mx-auto px-4 max-w-2xl">
                    <div className="bg-white rounded-lg shadow-lg p-8 text-center leading-relaxed">
                      <div className="mb-6">
                        <Clock className="mx-auto h-16 w-16 text-yellow-400" />
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-4">Get Ready!</h2>
                      <p className="text-gray-600 mb-6">
                        This exciting campaign will start on {new Date(campaign.start_date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric',
                          timeZone: 'Australia/Sydney'
                        })}.
                      </p>
                      <p className="text-sm text-gray-500">
                        Please check back then to participate!
                      </p>
                    </div>
                  </div>
                </main>
              </div>
            )
          ) : campaign?.display_status === 'draft' ? (
            <div className="min-h-screen bg-teal-50">
              <header className="relative overflow-hidden">
                <div
                  className="relative w-full py-12 md:py-16 px-4"
                  style={{
                    background: 'linear-gradient(135deg, #042f2e 0%, #0f766e 50%, #14b8a6 100%)',
                  }}
                >
                  <div className="absolute bottom-0 left-0 right-0 h-12 bg-teal-50" style={{ clipPath: 'ellipse(75% 100% at 50% 100%)' }} />
                  <div className="container mx-auto relative z-10 text-center">
                    {campaign?.logo_url && (
                      <img
                        src={`${ASSET_BASE_URL}${campaign.logo_url}`}
                        alt={campaign.name}
                        className="h-14 md:h-20 mx-auto mb-6 opacity-95"
                      />
                    )}
                    <h1 className="text-3xl md:text-5xl font-bold text-white tracking-tight drop-shadow-sm">
                      Campaign Not Available
                    </h1>
                    <p className="mt-3 text-teal-200 text-lg md:text-xl font-medium">
                      This campaign is currently in draft.
                    </p>
                  </div>
                </div>
              </header>

              <main className="py-14 md:py-20 px-4">
                <div className="container mx-auto max-w-xl">
                  <div
                    className="bg-white rounded-2xl shadow-xl shadow-teal-900/5 p-10 md:p-12 text-center border border-teal-100"
                  >
                    <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-teal-100">
                      <XCircle className="h-12 w-12 text-teal-700" aria-hidden />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-teal-900 mb-4">
                      Not Available
                    </h2>
                    <p className="text-teal-800/90 text-lg leading-relaxed mb-6">
                      This campaign is currently in draft and isn’t live yet.
                    </p>
                    <p className="text-sm text-teal-700/80">
                      Please contact support if you believe this is an error.
                    </p>
                  </div>
                </div>
              </main>
            </div>
          ) : campaign?.display_status !== 'active' ? (
            <div className="min-h-screen bg-gray-50">
              {/* Header with campaign theme */}
              <header className="bg-gray-50">
                <div className="pt-6 pb-4">
                  <div className="container mx-auto px-4">
                    {campaign?.logo_url && (
                      <img 
                        src={`${ASSET_BASE_URL}${campaign.logo_url}`} 
                        alt={campaign.name}
                        className="h-16 md:h-20"
                      />
                    )}
                  </div>
                </div>
                <div className="pb-6">
                  <div className="container mx-auto px-4">
                    {campaign?.background_image_url ? (
                      <div 
                        className="relative h-[47.1vw] md:h-[47.1vw] lg:aspect-[2933/1382] bg-cover bg-center bg-no-repeat rounded-lg shadow-lg overflow-hidden"
                        style={{
                          backgroundColor: campaign?.primary_color || '#1F2937',
                          backgroundImage: `url(${ASSET_BASE_URL}${campaign.background_image_url})`
                        }}
                      >
                        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                          <div className="text-center leading-relaxed text-white">
                            <h1 className="text-4xl md:text-6xl font-bold">Campaign Not Available</h1>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div 
                        className="h-32 rounded-lg shadow-lg flex items-center justify-center"
                        style={{ backgroundColor: campaign?.primary_color || '#1F2937' }}
                      >
                        <h1 className="text-3xl md:text-4xl font-bold text-white">Campaign Not Available</h1>
                      </div>
                    )}
                  </div>
                </div>
              </header>

              <main className="py-12">
                <div className="container mx-auto px-4 max-w-2xl">
                  <div className="bg-white rounded-lg shadow-lg p-8 text-center leading-relaxed">
                    <div className="mb-6">
                      <XCircle className="mx-auto h-16 w-16 text-gray-400" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Not Available</h2>
                    <p className="text-gray-600 mb-6">
                      This campaign is currently {campaign.display_status}.
                    </p>
                    <p className="text-sm text-gray-500">
                      Please contact support if you believe this is an error.
                    </p>
                  </div>
                </div>
              </main>
            </div>
          ) : (
            <>
              {/* Header with campaign theme */}
              <header className="bg-gray-50">
                {/* Logo Section */}
                <div className="pt-6 pb-4">
                  <div className="container mx-auto px-4">
                    {campaign?.logo_url && (
                      <img 
                        src={`${ASSET_BASE_URL}${campaign.logo_url}`} 
                        alt={campaign.name}
                        className="h-16 md:h-20"
                      />
                    )}
                  </div>
                </div>
            
          </header>

          {/* Main Content Area */}
          <main className="bg-gray-50">
            {/* Render sections - use new section system if available, otherwise fall back to legacy */}
            {sections.length > 0 ? (
              // New sections system - render each section instance
              sections.map((section, index) => renderSectionInstance(section, index))
            ) : (
              // Legacy system - render sections based on order
              sectionOrder.map(sectionId => renderLegacySection(sectionId))
            )}
            
            {/* Always render contact form at the bottom */}
            {renderLegacySection('contact')}
            
            {/* Always render terms footer after contact form */}
            {renderLegacySection('terms-footer')}
            
          </main>
            </>
          )}
        </>
      )}
      
      {/* Terms and Conditions Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gray-900 text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-semibold">Terms and Conditions</h3>
              <div className="flex items-center space-x-4">
                <a
                  href={getSecureAssetUrl(campaign?.terms_and_conditions)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download PDF
                </a>
                <button
                  onClick={() => setShowTermsModal(false)}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="p-6">
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-center justify-center space-x-2 text-gray-600 mb-4">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <span className="font-semibold text-lg">Terms and Conditions Document</span>
                </div>
                <p className="text-center leading-relaxed text-gray-600 mb-6">
                  Please review the terms and conditions before participating in this campaign.
                </p>
                <div className="bg-white rounded-lg overflow-hidden shadow-sm">
                  <iframe
                    src={`${getSecureAssetUrl(campaign?.terms_and_conditions)}#toolbar=0&navpanes=0&scrollbar=1`}
                    className="w-full h-[600px] border-0"
                    title="Terms and Conditions PDF"
                  />
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-500 text-center leading-relaxed">
                  By participating in this campaign, you agree to the terms and conditions outlined above.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CampaignPublic;
